import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent as ReactDragEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AddIcon from '@mui/icons-material/Add';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box, Button, ButtonGroup, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, Menu, MenuItem, Stack, TextField, Tooltip as MuiTooltip, Typography, useTheme } from '@mui/material';
import { getTraceChrome, tracePanelSx, tracePickerDrawerPaperSx } from '@kangaroo/ui';
import { hasValidSavedTracePairs } from '@kangaroo/shared';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { SessionCompareReplayLayer } from '../../features/sessions/sessionCompareMap';
import {
  buildTraceAlignmentMappers,
  type TraceAlignmentMapper,
  type TraceAlignmentMode,
} from '../../lib/traceAlignment';
export {
  buildTraceAlignmentMappers,
  type TraceAlignmentMapper,
  type TraceAlignmentMode,
} from '../../lib/traceAlignment';
import { useSavedTraceViews } from '../../hooks/useSavedTraceViews';
import { PendingTraceViewCreate, pairSavedTraceWithPrevious, retainSelectedTracePairs, readLegacyTraceViews, readSelectedCloudTraceView, writeSelectedCloudTraceView, traceViewBrowserStorage, type CloudTraceView, type LegacyTraceView } from '../../lib/savedTraceViews';
import { useMeasurementPreferences } from '../../hooks/useMeasurementPreferences';
import { convertSpeedKph, formatMeasurement, speedUnitLabel } from '../../lib/measurements';
import { formatSpeedDeltaFromKph } from '../../lib/telemetryMeasurements';
import {
  readSessionReviewPreferences,
  updateSessionReviewPreferences,
} from '../../lib/sessionReviewPreferences';
import {
  autoSeriesTraceDomain,
  buildGenericSeriesTrace,
  defaultEnabledTraceChannels,
  formatSeriesTraceValue,
  formatTraceClock,
  listAllTraceChannels,
  listPopulatedTraceChannels,
  type SeriesTracePoint,
  seriesValueAtTime,
  speedKphAtTime,
} from '../../lib/telemetrySession';

const TRACE_CHART_MARGIN = { top: 4, right: 8, bottom: 0, left: -18 } as const;
const TRACE_GRAPH_HEIGHTS_STORAGE_KEY = 'paddiq.trace-graph-heights.v1';
const SESSION_STORYBOARD_HEIGHT_STORAGE_KEY = 'paddiq.session-storyboard-height.v1';
const BUILT_IN_ENGINE_TRACE_PRESETS = [
  { id: 'builtin:lo206', name: 'LO206', family: 'four-stroke' },
  { id: 'builtin:iame-ka100', name: 'IAME KA100', family: 'iame' },
  { id: 'builtin:iame-x30-junior', name: 'IAME X30 Junior', family: 'iame' },
  { id: 'builtin:iame-x30-senior', name: 'IAME X30 Senior', family: 'iame' },
  { id: 'builtin:iame-mini-swift', name: 'IAME Mini Swift', family: 'iame' },
] as const;

export type TraceTimeRange = { startSec: number; endSec: number };

export type TraceSessionLapWindow = {
  lapNumber: number;
  startMs: number;
  endMs: number;
  label: string;
  selectable: boolean;
};

export type TraceSessionTimeline = {
  startMs: number;
  endMs: number;
  durationSec: number;
  laps: Array<TraceSessionLapWindow & { startSec: number; endSec: number; durationSec: number }>;
};

export type TraceSessionGpsCoordinateLine = {
  extentMeters: number;
  points: Array<{ x: number; y: number }>;
};

const MIN_TRACE_ZOOM_SPAN_SEC = 5;
const MIN_TRACE_GRAPH_HEIGHT = 56;
const MAX_TRACE_GRAPH_HEIGHT = 480;
const DEFAULT_SESSION_STORYBOARD_HEIGHT = 82;
const MIN_SESSION_STORYBOARD_HEIGHT = 64;
const MAX_SESSION_STORYBOARD_HEIGHT = 280;

export function clampSessionStoryboardHeight(height: number) {
  return Math.min(
    MAX_SESSION_STORYBOARD_HEIGHT,
    Math.max(MIN_SESSION_STORYBOARD_HEIGHT, Math.round(height)),
  );
}

function readSessionStoryboardHeight() {
  if (typeof window === 'undefined') return DEFAULT_SESSION_STORYBOARD_HEIGHT;
  const value = Number(window.localStorage.getItem(SESSION_STORYBOARD_HEIGHT_STORAGE_KEY));
  return Number.isFinite(value) && value > 0
    ? clampSessionStoryboardHeight(value)
    : DEFAULT_SESSION_STORYBOARD_HEIGHT;
}

export function buildTraceSessionTimeline(
  windows: TraceSessionLapWindow[],
): TraceSessionTimeline | null {
  const validWindows = windows
    .filter(
      (window) =>
        Number.isFinite(window.startMs)
        && Number.isFinite(window.endMs)
        && window.endMs > window.startMs,
    )
    .sort((left, right) => left.startMs - right.startMs || left.endMs - right.endMs);

  if (validWindows.length === 0) {
    return null;
  }

  const startMs = Math.min(...validWindows.map((window) => window.startMs));
  const endMs = Math.max(...validWindows.map((window) => window.endMs));
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return null;
  }

  return {
    startMs,
    endMs,
    durationSec: (endMs - startMs) / 1000,
    laps: validWindows.map((window) => ({
      ...window,
      startSec: (window.startMs - startMs) / 1000,
      endSec: (window.endMs - startMs) / 1000,
      durationSec: (window.endMs - window.startMs) / 1000,
    })),
  };
}

export function buildTraceSessionGpsCoordinateLine(
  gps: Array<{ timecodeMs: number; lat: number; lon: number }>,
  timeline: TraceSessionTimeline,
  maxPoints = 600,
): TraceSessionGpsCoordinateLine | null {
  const samples = gps
    .filter(
      (point) =>
        Number.isFinite(point.timecodeMs)
        && Number.isFinite(point.lat)
        && Number.isFinite(point.lon)
        && point.timecodeMs >= timeline.startMs
        && point.timecodeMs <= timeline.endMs,
    )
    .sort((left, right) => left.timecodeMs - right.timecodeMs);

  if (samples.length < 2 || maxPoints < 2) return null;

  const meanLat = samples.reduce((sum, point) => sum + point.lat, 0) / samples.length;
  const meanLon = samples.reduce((sum, point) => sum + point.lon, 0) / samples.length;
  const metersPerDegreeLat = 110_540;
  const metersPerDegreeLon = 111_320 * Math.cos((meanLat * Math.PI) / 180);
  const localCoordinates = samples.map((point) => ({
    x: (point.lon - meanLon) * metersPerDegreeLon,
    y: (point.lat - meanLat) * metersPerDegreeLat,
  }));
  const covariance = localCoordinates.reduce(
    (result, point) => ({
      xx: result.xx + point.x * point.x,
      xy: result.xy + point.x * point.y,
      yy: result.yy + point.y * point.y,
    }),
    { xx: 0, xy: 0, yy: 0 },
  );
  const axisAngle = 0.5 * Math.atan2(2 * covariance.xy, covariance.xx - covariance.yy);
  let axisX = Math.cos(axisAngle);
  let axisY = Math.sin(axisAngle);
  const first = localCoordinates[0]!;
  const last = localCoordinates.at(-1)!;
  if ((last.x - first.x) * axisX + (last.y - first.y) * axisY < 0) {
    axisX *= -1;
    axisY *= -1;
  }
  const projections = localCoordinates.map((point) => point.x * axisX + point.y * axisY);
  const minProjection = Math.min(...projections);
  const maxProjection = Math.max(...projections);
  const extentMeters = maxProjection - minProjection;
  const sampled = samples.length <= maxPoints
    ? samples.map((point, index) => ({ point, projection: projections[index]! }))
    : Array.from({ length: maxPoints }, (_, index) => (
        (() => {
          const sampleIndex = Math.round((index * (samples.length - 1)) / (maxPoints - 1));
          return { point: samples[sampleIndex]!, projection: projections[sampleIndex]! };
        })()
      ));

  return {
    extentMeters,
    points: sampled.map(({ point, projection }) => ({
      x: Number((
        ((point.timecodeMs - timeline.startMs) / (timeline.endMs - timeline.startMs)) * 1000
      ).toFixed(3)),
      y: Number((extentMeters > 0
        ? 92 - ((projection - minProjection) / extentMeters) * 84
        : 50
      ).toFixed(3)),
    })),
  };
}

export function defaultTraceChartDimensions(fillHeight: boolean) {
  const chartHeight = fillHeight ? 168 : 104;

  return { chartHeight, rowMinHeight: chartHeight + (fillHeight ? 16 : 8) };
}

export function clampTraceChartHeight(height: number, fillHeight: boolean) {
  const maximum = fillHeight ? MAX_TRACE_GRAPH_HEIGHT : 320;
  return Math.min(maximum, Math.max(MIN_TRACE_GRAPH_HEIGHT, Math.round(height)));
}

export function traceChartMemoryKey(traceKey: string, fillHeight: boolean) {
  return `${fillHeight ? 'full' : 'compact'}:${traceKey}`;
}

type TraceWheelGesture = {
  ctrlKey: boolean;
  deltaMode: number;
  deltaX: number;
  wheelDeltaY?: number;
};

export function isTraceZoomWheelGesture(event: TraceWheelGesture) {
  // Browser trackpad pinches arrive as pixel-mode wheel events with ctrlKey set.
  if (event.ctrlKey) return true;

  // Firefox and some Chromium configurations report physical mouse wheels in
  // line or page units. Pixel-mode two-finger scrolling must remain available
  // to the page instead of being captured by the chart.
  if (event.deltaMode !== 0) return true;
  if (Math.abs(event.deltaX) > 0.5) return false;

  // Chromium/WebKit expose the legacy 120-unit wheel notch even when deltaMode
  // is pixel-based. Trackpads report smooth, non-notched values here.
  const legacyDelta = Math.abs(event.wheelDeltaY ?? 0);
  if (legacyDelta < 120) return false;
  return Math.abs(legacyDelta / 120 - Math.round(legacyDelta / 120)) < 0.001;
}

export function nextTraceZoomRange(
  current: TraceTimeRange | null,
  durationSec: number,
  anchorSec: number,
  direction: 'in' | 'out',
): TraceTimeRange | null {
  if (!Number.isFinite(durationSec) || durationSec <= MIN_TRACE_ZOOM_SPAN_SEC) return null;

  const fullRange = { startSec: 0, endSec: durationSec };
  const activeRange = current && current.endSec > current.startSec
    ? {
        startSec: Math.max(0, Math.min(current.startSec, durationSec)),
        endSec: Math.max(0, Math.min(current.endSec, durationSec)),
      }
    : fullRange;
  const activeSpan = activeRange.endSec - activeRange.startSec;

  if (direction === 'out') {
    if (!current) return null;
    const targetSpan = Math.min(durationSec, activeSpan * 2);
    if (targetSpan >= durationSec - 0.01) return null;
    const center = (activeRange.startSec + activeRange.endSec) / 2;
    const startSec = Math.min(
      durationSec - targetSpan,
      Math.max(0, center - targetSpan / 2),
    );
    return { startSec, endSec: startSec + targetSpan };
  }

  if (activeSpan <= MIN_TRACE_ZOOM_SPAN_SEC + 0.01) return current;
  const targetSpan = Math.max(MIN_TRACE_ZOOM_SPAN_SEC, activeSpan / 2);
  const anchor = Math.min(activeRange.endSec, Math.max(activeRange.startSec, anchorSec));
  const startSec = Math.min(
    activeRange.endSec - targetSpan,
    Math.max(activeRange.startSec, anchor - targetSpan / 2),
  );
  return { startSec, endSec: startSec + targetSpan };
}

function readTraceGraphHeights(): Record<string, number> {
  if (typeof window === 'undefined') return {};

  try {
    const saved = JSON.parse(window.localStorage.getItem(TRACE_GRAPH_HEIGHTS_STORAGE_KEY) ?? '{}');
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};
    return Object.fromEntries(
      Object.entries(saved).flatMap(([key, height]) => (
        typeof height === 'number' && Number.isFinite(height)
          ? [[key, height]]
          : []
      )),
    );
  } catch {
    return {};
  }
}

function writeTraceGraphHeights(heights: Record<string, number>) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(TRACE_GRAPH_HEIGHTS_STORAGE_KEY, JSON.stringify(heights));
  }
}

function isBuiltInTraceViewId(id: string) {
  return BUILT_IN_ENGINE_TRACE_PRESETS.some((preset) => preset.id === id);
}

function sameTraceKeys(left: string[], right: string[]) {
  return left.length === right.length && left.every((key, index) => key === right[index]);
}

type SessionCompareTracePanelProps = {
  layers: SessionCompareReplayLayer[];
  cursorSec: number;
  durationSec: number;
  alignmentMode?: TraceAlignmentMode;
  onSeekSec: (elapsedSec: number) => void;
  drawer?: boolean;
  fillHeight?: boolean;
  tracePickerOpen?: boolean;
  onTracePickerClose?: () => void;
  saveDialogOpen?: boolean;
  onSaveDialogClose?: () => void;
  selectedRange?: { startSec: number; endSec: number } | null;
  onSelectRange?: (startSec: number, endSec: number) => void;
  onReplaySelection?: () => void;
  onClearSelection?: () => void;
  onTraceZoomRangeChange?: (range: TraceTimeRange | null) => void;
};

type MergedTraceRow = {
  elapsedSec: number;
  [layerId: string]: number | null;
};

function alignTracePoints(
  points: SeriesTracePoint[],
  mapper: TraceAlignmentMapper | undefined,
): SeriesTracePoint[] {
  if (!mapper) return points;
  return points.map((point) => ({
    ...point,
    elapsedSec: mapper.toAlignedSec(point.elapsedSec),
  }));
}

function traceBounds<T extends { elapsedSec: number }>(points: T[], elapsedSec: number) {
  if (points.length === 0) {
    return { prev: null, next: null };
  }

  let low = 0;
  let high = points.length - 1;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (points[mid]!.elapsedSec < elapsedSec) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  const next = points[low] ?? null;
  const prev = low > 0 ? points[low - 1]! : null;

  return { prev, next };
}

export function interpolateValue(
  prev: MergedTraceRow | SeriesTracePoint | { elapsedSec: number; deltaKph: number } | null,
  next: MergedTraceRow | SeriesTracePoint | { elapsedSec: number; deltaKph: number } | null,
  elapsedSec: number,
  getValue: (point: MergedTraceRow | SeriesTracePoint | { elapsedSec: number; deltaKph: number }) => number | null | undefined,
) {
  if (!Number.isFinite(elapsedSec)) return null;
  if (next?.elapsedSec === elapsedSec) {
    const value = getValue(next);
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
  if (prev?.elapsedSec === elapsedSec) {
    const value = getValue(prev);
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
  if (!prev || !next || elapsedSec < prev.elapsedSec || elapsedSec > next.elapsedSec) return null;
  const prevValue = prev ? getValue(prev) : null;
  const nextValue = next ? getValue(next) : null;

  if (typeof prevValue !== 'number' || !Number.isFinite(prevValue)) {
    return null;
  }

  if (typeof nextValue !== 'number' || !Number.isFinite(nextValue)) {
    return null;
  }

  const span = next!.elapsedSec - prev!.elapsedSec;
  if (span <= 0) {
    return prevValue;
  }

  const progress = Math.min(1, Math.max(0, (elapsedSec - prev!.elapsedSec) / span));
  return prevValue + (nextValue - prevValue) * progress;
}

export function interpolatedTraceValue(trace: SeriesTracePoint[], elapsedSec: number) {
  const { prev, next } = traceBounds(trace, elapsedSec);
  return interpolateValue(prev, next, elapsedSec, (point) => (point as SeriesTracePoint).value);
}

function interpolatedRowValue(rows: MergedTraceRow[], elapsedSec: number, key: string) {
  const { prev, next } = traceBounds(rows, elapsedSec);
  return interpolateValue(prev, next, elapsedSec, (point) => (point as MergedTraceRow)[key]);
}

function interpolatedSpeedDelta(
  points: Array<{ elapsedSec: number; deltaKph: number }>,
  elapsedSec: number,
) {
  const { prev, next } = traceBounds(points, elapsedSec);
  return interpolateValue(prev, next, elapsedSec, (point) => (
    point as { elapsedSec: number; deltaKph: number }
  ).deltaKph);
}

function mergeLayerTraces(
  layers: SessionCompareReplayLayer[],
  shortName: string,
  alignmentMappers?: Map<string, TraceAlignmentMapper>,
): MergedTraceRow[] {
  const traces = layers.map((layer) =>
    alignTracePoints(buildGenericSeriesTrace(
      layer.sessionExport,
      shortName,
      layer.analysis.startMs,
      layer.analysis.endMs,
      layer.analysis.startMs,
    ), alignmentMappers?.get(layer.id)),
  );

  return mergeSampledTraces(layers.map((layer, index) => ({ id: layer.id, points: traces[index]! })));
}

export function mergeSampledTraces(traces: Array<{ id: string; points: SeriesTracePoint[] }>): MergedTraceRow[] {
  const timestamps = [...new Set(traces.flatMap((trace) => trace.points.map((point) => point.elapsedSec)))].sort((a, b) => a - b);
  return timestamps.map((elapsedSec) => ({
    elapsedSec,
    ...Object.fromEntries(traces.map((trace) => [trace.id, interpolatedTraceValue(trace.points, elapsedSec)])),
  }));
}

function groundSpeedTrace(layer: SessionCompareReplayLayer): SeriesTracePoint[] {
  return layer.points.filter((point) => point.timecodeMs >= layer.analysis.startMs && point.timecodeMs <= layer.analysis.endMs)
    .map((point, index) => ({ index, timecodeMs: point.timecodeMs, elapsedSec: (point.timecodeMs - layer.analysis.startMs) / 1000, value: point.speedKph }));
}

export function interpolatedLayerGroundSpeed(layer: SessionCompareReplayLayer, elapsedSec: number) {
  const targetMs = layer.analysis.startMs + elapsedSec * 1000;
  const points = layer.points;
  if (!Number.isFinite(targetMs) || targetMs < layer.analysis.startMs || targetMs > layer.analysis.endMs
    || !points.length || targetMs < points[0]!.timecodeMs || targetMs > points.at(-1)!.timecodeMs) return null;
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (points[mid]!.timecodeMs < targetMs) low = mid + 1;
    else high = mid;
  }
  const next = points[low]!;
  if (!Number.isFinite(next.speedKph)) return null;
  if (next.timecodeMs === targetMs) return next.speedKph;
  const prev = points[low - 1];
  if (!prev || !Number.isFinite(prev.speedKph)) return null;
  const spanMs = next.timecodeMs - prev.timecodeMs;
  // Sorted duplicate timestamps normally take the exact-match path above.
  // Reject degenerate intervals explicitly rather than emitting invalid speeds.
  if (!Number.isFinite(spanMs) || spanMs <= 0) return null;
  const ratio = (targetMs - prev.timecodeMs) / spanMs;
  const speedKph = prev.speedKph + (next.speedKph - prev.speedKph) * ratio;
  return Number.isFinite(speedKph) ? speedKph : null;
}

export function mergeGroundSpeedTraces(
  layers: SessionCompareReplayLayer[],
  alignmentMappers?: Map<string, TraceAlignmentMapper>,
): MergedTraceRow[] {
  return mergeSampledTraces(layers.map((layer) => ({
    id: layer.id,
    points: alignTracePoints(groundSpeedTrace(layer), alignmentMappers?.get(layer.id)),
  })));
}

function gpsCoordinateTrace(
  layer: SessionCompareReplayLayer,
  coordinate: 'lat' | 'lon',
): SeriesTracePoint[] {
  return layer.sessionExport.gps
    .filter(
      (point) =>
        point.timecodeMs >= layer.analysis.startMs
        && point.timecodeMs <= layer.analysis.endMs
        && Number.isFinite(point[coordinate]),
    )
    .map((point, index) => ({
      index,
      timecodeMs: point.timecodeMs,
      elapsedSec: (point.timecodeMs - layer.analysis.startMs) / 1000,
      value: point[coordinate],
    }));
}

function mergeGpsCoordinateTraces(
  layers: SessionCompareReplayLayer[],
  coordinate: 'lat' | 'lon',
  alignmentMappers?: Map<string, TraceAlignmentMapper>,
): MergedTraceRow[] {
  return mergeSampledTraces(layers.map((layer) => ({
    id: layer.id,
    points: alignTracePoints(gpsCoordinateTrace(layer, coordinate), alignmentMappers?.get(layer.id)),
  })));
}

const SPEED_DELTA_TRACE_KEY = 'speed-delta';
const SPEED_DELTA_POSITIVE_COLOR = '#22C55E';
const SPEED_DELTA_NEGATIVE_COLOR = '#F87171';
const SPEED_TRACE_KEY = 'speed';
const GPS_LATITUDE_TRACE_KEY = 'gps-latitude';
const GPS_LONGITUDE_TRACE_KEY = 'gps-longitude';
const SPEED_HISTOGRAM_TRACE_KEY = 'speed-histogram';
const RPM_HISTOGRAM_TRACE_KEY = 'rpm-histogram';
const LAP_LINE_DASHES = ['', '7 4', '2 3', '9 3 2 3', '1 3', '11 4'] as const;
const COMMON_TRACE_MATCHERS = [
  /\b(speed|wspd|wheel speed)\b/i,
  /\b(rpm|engine rpm)\b/i,
  /(steer|stra)/i,
  /(long g|accx|longitudinal)/i,
  /(lat g|accy|lateral)/i,
  /(brake|brkp|pbrk)/i,
  /(throttle|tps|pedal)/i,
  /gear/i,
] as const;

const TRACE_GROUP_ORDER = [
  'Common traces',
  'Compare traces',
  'Driver inputs',
  'Vehicle dynamics',
  'Engine & drivetrain',
  'Temperatures & pressures',
  'Electrical & system',
  'GPS & environment',
  'Analysis',
  'Other channels',
] as const;

type TraceOption = {
  key: string;
  label: string;
  sublabel: string;
  color: string;
  kind: 'series' | 'speed-delta' | 'ground-speed' | 'gps-coordinate' | 'histogram';
  gpsCoordinate?: 'lat' | 'lon';
  histogramMetric?: 'speed' | 'rpm';
  shortName?: string;
  unit?: string;
  decimals?: number;
  sampleCount?: number;
  description?: string;
};

function traceOptionSupportsPair(option: TraceOption | null | undefined) {
  return option?.kind === 'series'
    || option?.kind === 'ground-speed'
    || option?.kind === 'gps-coordinate';
}

function traceChannelDescription(shortName: string, longName: string, unit: string) {
  const value = `${shortName} ${longName}`.toLowerCase();
  const measuredUnit = unit ? ` Reported in ${unit}.` : '';

  if (/\brpm\b|engine speed/.test(value)) return `Engine rotational speed, used to assess gearing, shifts, and acceleration.${measuredUnit}`;
  if (/wspd|wheel speed/.test(value)) return `Wheel-speed sensor measurement, useful for acceleration, braking, and wheel-slip analysis.${measuredUnit}`;
  if (/stra|steer/.test(value)) return `Steering input or steering-wheel angle, showing how much lock the driver applies.${measuredUnit}`;
  if (/accx|longitudinal/.test(value)) return `Longitudinal acceleration: braking and forward acceleration along the kart.${measuredUnit}`;
  if (/accy|lateral/.test(value)) return `Lateral acceleration generated while cornering.${measuredUnit}`;
  if (/accz|vertical/.test(value)) return `Vertical acceleration from bumps, kerbs, and chassis movement.${measuredUnit}`;
  if (/gyrx|gyro.?x|roll rate/.test(value)) return `Rotation rate around the longitudinal axis, describing roll motion.${measuredUnit}`;
  if (/gyry|gyro.?y|pitch rate/.test(value)) return `Rotation rate around the lateral axis, describing pitch motion.${measuredUnit}`;
  if (/gyrz|gyro.?z|yaw rate/.test(value)) return `Rotation rate around the vertical axis, describing how quickly the kart turns.${measuredUnit}`;
  if (/quaw|quaternion.?w/.test(value)) return 'Scalar component of the orientation quaternion used to reconstruct logger attitude.';
  if (/quax|quaternion.?x/.test(value)) return 'X component of the orientation quaternion used to reconstruct logger attitude.';
  if (/quay|quaternion.?y/.test(value)) return 'Y component of the orientation quaternion used to reconstruct logger attitude.';
  if (/quaz|quaternion.?z/.test(value)) return 'Z component of the orientation quaternion used to reconstruct logger attitude.';
  if (/yawa|yaw angle|heading/.test(value)) return `Kart heading or yaw orientation around the vertical axis.${measuredUnit}`;
  if (/pitch/.test(value)) return `Kart nose-up or nose-down orientation.${measuredUnit}`;
  if (/roll|lean|bank/.test(value)) return `Kart side-to-side orientation or chassis roll.${measuredUnit}`;
  if (/brake|brkp|pbrk/.test(value)) return `Driver brake input or braking-system pressure.${measuredUnit}`;
  if (/throttle|tps|pedal/.test(value)) return `Driver throttle input or throttle-position sensor reading.${measuredUnit}`;
  if (/gear/.test(value)) return `Selected or calculated transmission gear.${measuredUnit}`;
  if (/lambda|afr|air.?fuel/.test(value)) return `Engine air-fuel mixture measurement used for tuning.${measuredUnit}`;
  if (/egt|exhaust.*temp/.test(value)) return `Exhaust-gas temperature used to monitor combustion and engine tuning.${measuredUnit}`;
  if (/coolant|water.*temp/.test(value)) return `Engine coolant temperature used to monitor operating temperature.${measuredUnit}`;
  if (/oil.*temp/.test(value)) return `Engine or gearbox oil temperature.${measuredUnit}`;
  if (/oil.*press/.test(value)) return `Lubrication-system oil pressure.${measuredUnit}`;
  if (/temp|therm|tc\s*\d|logt/.test(value)) return `Temperature measured by the named logger or external sensor.${measuredUnit}`;
  if (/press|baro/.test(value)) return `Pressure measured by the named sensor.${measuredUnit}`;
  if (/vbat|battery voltage/.test(value)) return `Vehicle battery or electrical-system voltage.${measuredUnit}`;
  if (/vbin|internal.*batt/.test(value)) return `Logger internal battery voltage.${measuredUnit}`;
  if (/volt/.test(value)) return `Electrical potential measured by the named input.${measuredUnit}`;
  if (/current|amp/.test(value)) return `Electrical current measured by the named input.${measuredUnit}`;
  if (/latitude/.test(value)) return `GPS north-south position.${measuredUnit}`;
  if (/longitude/.test(value)) return `GPS east-west position.${measuredUnit}`;
  if (/altitude|elevation/.test(value)) return `Height above the GPS reference level.${measuredUnit}`;
  if (/satellite/.test(value)) return 'Number or quality of GPS satellites contributing to the position fix.';
  if (/distance|odometer/.test(value)) return `Distance accumulated by the logger or sensor.${measuredUnit}`;
  if (/luma|luminos/.test(value)) return `Ambient light level measured by the logger.${measuredUnit}`;
  if (/speed|velocity/.test(value)) return `Speed reported by the named sensor or calculated source.${measuredUnit}`;
  if (/status|switch|button/.test(value)) return 'State of the named digital input or logger function.';
  if (/signal|quality/.test(value)) return 'Signal strength or quality reported by the named sensor.';

  return `${longName || shortName} telemetry recorded by the logger.${measuredUnit}`;
}

function traceOptionTooltip(option: TraceOption) {
  const unit = option.unit ? `Unit: ${option.unit}` : 'Unit: not specified';
  const samples = option.kind === 'series'
    ? `${(option.sampleCount ?? 0).toLocaleString()} samples in this lap`
    : 'Calculated view';
  return `${option.description ?? option.sublabel} ${unit} · ${samples}`;
}

function commonTraceRank(option: TraceOption) {
  const searchable = `${option.key} ${option.label} ${option.shortName ?? ''}`;
  return COMMON_TRACE_MATCHERS.findIndex((matcher) => matcher.test(searchable));
}

function traceGroupForOption(option: TraceOption): (typeof TRACE_GROUP_ORDER)[number] {
  if (option.kind === 'speed-delta') return 'Compare traces';
  if (option.kind === 'histogram') return 'Analysis';
  if (option.kind === 'ground-speed' || commonTraceRank(option) >= 0) return 'Common traces';

  const value = `${option.key} ${option.label} ${option.shortName ?? ''}`.toLowerCase();
  if (/(brake|throttle|pedal|steer|gear|clutch|switch|button)/.test(value)) return 'Driver inputs';
  if (/(accel|acc[xyz]|gyro|gyr[xyz]|yaw|pitch|roll|quat|qua[xyzw]|speed|velocity|lean|bank)/.test(value)) return 'Vehicle dynamics';
  if (/(rpm|engine|motor|lambda|afr|fuel|inject|ignition|wheel|drive|shaft|torque|power)/.test(value)) return 'Engine & drivetrain';
  if (/(temp|pressure|press|therm|coolant|water|oil|exhaust|egt|baro)/.test(value)) return 'Temperatures & pressures';
  if (/(volt|battery|current|amp|vbat|vbin|signal|status|logger|logt)/.test(value)) return 'Electrical & system';
  if (/(gps|latitude|longitude|altitude|satellite|heading|course|distance|luma|luminos|ambient)/.test(value)) return 'GPS & environment';
  return 'Other channels';
}

type HistogramRow = {
  bucket: string;
  bucketStart: number;
  [layerId: string]: string | number;
};

function buildHistogramRows(
  layers: SessionCompareReplayLayer[],
  metric: 'speed' | 'rpm',
  measurementPreferences: ReturnType<typeof useMeasurementPreferences>,
): HistogramRow[] {
  if (layers.length === 0) {
    return [];
  }

  const valuesByLayer = layers.map((layer) => {
    if (metric === 'speed') {
      return layer.points.map((point) => ({
        timecodeMs: point.timecodeMs,
        value: convertSpeedKph(point.speedKph, measurementPreferences),
      }));
    }

    return buildGenericSeriesTrace(
      layer.sessionExport,
      'RPM',
      layer.analysis.startMs,
      layer.analysis.endMs,
      layer.analysis.startMs,
    )
      .flatMap((point) => (
        point.value === null || !Number.isFinite(point.value)
          ? []
          : [{ timecodeMs: point.timecodeMs, value: point.value }]
      ));
  });

  const allValues = valuesByLayer.flatMap((samples) => samples.map((sample) => sample.value));
  if (allValues.length === 0) {
    return [];
  }

  const bucketSize = metric === 'speed'
    ? measurementPreferences.speedUnit === 'MPH' ? 5 : 10
    : 500;
  const minBucket = Math.floor(Math.min(...allValues) / bucketSize) * bucketSize;
  const maxBucket = Math.ceil(Math.max(...allValues) / bucketSize) * bucketSize;
  const bucketStarts: number[] = [];
  for (let bucket = minBucket; bucket <= maxBucket; bucket += bucketSize) {
    bucketStarts.push(bucket);
  }

  const rows = new Map<number, HistogramRow>();
  for (const bucketStart of bucketStarts) {
    rows.set(bucketStart, {
      bucket: metric === 'speed'
        ? `${formatMeasurement(bucketStart, 0)}-${formatMeasurement(bucketStart + bucketSize, 0)} ${speedUnitLabel(measurementPreferences)}`
        : `${bucketStart / 1000}-${(bucketStart + bucketSize) / 1000}k`,
      bucketStart,
    });
  }

  layers.forEach((layer, layerIndex) => {
    const samples = valuesByLayer[layerIndex] ?? [];
    for (let index = 0; index < samples.length; index += 1) {
      const sample = samples[index]!;
      const next = samples[index + 1];
      const durationSec = next ? Math.max(0, (next.timecodeMs - sample.timecodeMs) / 1000) : 0;
      const bucketStart = Math.floor(sample.value / bucketSize) * bucketSize;
      const row = rows.get(bucketStart);
      if (!row) {
        continue;
      }

      row[layer.id] = ((row[layer.id] as number | undefined) ?? 0) + durationSec;
    }
  });

  return [...rows.values()].filter((row) => layers.some((layer) => ((row[layer.id] as number | undefined) ?? 0) > 0));
}

export function layerSpeedKphAtElapsed(layer: SessionCompareReplayLayer, elapsedSec: number): number | null {
  if (!Number.isFinite(elapsedSec) || elapsedSec < 0 || elapsedSec > (layer.analysis.endMs - layer.analysis.startMs) / 1000) return null;
  const timecodeMs = layer.analysis.startMs + elapsedSec * 1000;
  return speedKphAtTime(layer.sessionExport, timecodeMs) ?? interpolatedLayerGroundSpeed(layer, elapsedSec);
}

function buildSpeedDeltaTrace(
  primary: SessionCompareReplayLayer,
  compare: SessionCompareReplayLayer,
  compareElapsedAtPrimaryElapsed: (elapsedSec: number) => number = (elapsedSec) => elapsedSec,
): Array<{ elapsedSec: number; deltaKph: number }> {
  const { startMs, endMs } = primary.analysis;

  const sampleAtElapsed = (elapsedSec: number) => {
    const primaryKph = layerSpeedKphAtElapsed(primary, elapsedSec);
    const compareKph = layerSpeedKphAtElapsed(
      compare,
      compareElapsedAtPrimaryElapsed(elapsedSec),
    );

    if (primaryKph === null || compareKph === null) {
      return null;
    }

    return { elapsedSec, deltaKph: compareKph - primaryKph };
  };

  if (primary.points.length >= 2) {
    return primary.points.flatMap((point) => {
      const elapsedSec = Math.max(0, (point.timecodeMs - startMs) / 1000);
      const sample = sampleAtElapsed(elapsedSec);
      return sample ? [sample] : [];
    });
  }

  const speedSamples = buildGenericSeriesTrace(
    primary.sessionExport,
    'WSpd',
    startMs,
    endMs,
    startMs,
  );
  const rpmSamples = buildGenericSeriesTrace(
    primary.sessionExport,
    'RPM',
    startMs,
    endMs,
    startMs,
  );
  const samples = rpmSamples.length > speedSamples.length ? rpmSamples : speedSamples;

  return samples.flatMap((point) => {
    const sample = sampleAtElapsed(point.elapsedSec);
    return sample ? [sample] : [];
  });
}

function splitSpeedDeltaTrace(points: Array<{ elapsedSec: number; deltaKph: number }>) {
  const rows: Array<{
    elapsedSec: number;
    deltaKph: number;
    deltaPos: number | null;
    deltaNeg: number | null;
  }> = [];

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index]!;
    const prev = index > 0 ? points[index - 1]! : null;

    if (
      prev
      && ((prev.deltaKph < 0 && point.deltaKph > 0) || (prev.deltaKph > 0 && point.deltaKph < 0))
      && point.deltaKph !== prev.deltaKph
    ) {
      const progress = (0 - prev.deltaKph) / (point.deltaKph - prev.deltaKph);
      const crossingSec = prev.elapsedSec + (point.elapsedSec - prev.elapsedSec) * progress;
      rows.push({
        elapsedSec: crossingSec,
        deltaKph: 0,
        deltaPos: 0,
        deltaNeg: 0,
      });
    }

    rows.push({
      elapsedSec: point.elapsedSec,
      deltaKph: point.deltaKph,
      deltaPos: point.deltaKph >= 0 ? point.deltaKph : null,
      deltaNeg: point.deltaKph <= 0 ? point.deltaKph : null,
    });
  }

  return rows;
}

function TraceChartShell({
  title,
  subtitle,
  liveValue,
  children,
  chrome,
  chartHeight,
  rowMinHeight,
  maximumChartHeight,
  traceKey,
  dragging = false,
  onTraceDragStart,
  onTraceDragEnd,
  onTraceDrop,
  onRemove,
  onAddTrace,
  onChartHeightChange,
  onChartHeightReset,
  legendItems = [],
}: {
  title: string;
  subtitle?: string;
  liveValue?: string;
  children: ReactNode;
  chrome: ReturnType<typeof getTraceChrome>;
  chartHeight: number;
  rowMinHeight: number;
  maximumChartHeight: number;
  traceKey: string;
  dragging?: boolean;
  onTraceDragStart: (key: string) => void;
  onTraceDragEnd: () => void;
  onTraceDrop: (key: string) => void;
  onRemove: () => void;
  onAddTrace?: (anchor: HTMLElement) => void;
  onChartHeightChange: (height: number) => void;
  onChartHeightReset: () => void;
  legendItems?: Array<{ label: string; color: string; dashPattern?: string }>;
}) {
  const [resizing, setResizing] = useState(false);
  const resizeStartRef = useRef({ clientY: 0, chartHeight });

  const setClampedChartHeight = (height: number) => {
    onChartHeightChange(Math.min(maximumChartHeight, Math.max(MIN_TRACE_GRAPH_HEIGHT, height)));
  };

  return (
    <Box
      data-trace-row
      sx={{
        display: 'grid',
        gridTemplateColumns: '148px minmax(0, 1fr) 72px',
        gap: 0.8,
        alignItems: 'center',
        position: 'relative',
        minHeight: rowMinHeight,
        borderBottom: `1px solid ${chrome.chartRowBorder}`,
        opacity: dragging ? 0.48 : 1,
        transition: 'opacity 120ms ease, background-color 120ms ease',
        '&:hover': { bgcolor: chrome.pickerItemHover },
      }}
      onDragOver={(event: ReactDragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(event: ReactDragEvent<HTMLDivElement>) => {
        event.preventDefault();
        onTraceDrop(traceKey);
      }}
    >
      <Stack direction="row" spacing={0.35} sx={{ alignItems: 'center', minWidth: 0 }}>
        <Box
          draggable
          aria-label={`Reorder ${title}`}
          title="Drag to reorder"
          onDragStart={(event: ReactDragEvent<HTMLDivElement>) => {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', traceKey);
            const row = event.currentTarget.closest<HTMLElement>('[data-trace-row]');
            if (row) {
              const rect = row.getBoundingClientRect();
              const ghost = row.cloneNode(true) as HTMLElement;
              Object.assign(ghost.style, {
                position: 'fixed',
                top: '-10000px',
                left: '-10000px',
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                opacity: '0.94',
                background: chrome.panelBg,
                border: `1px solid ${chrome.border}`,
                borderRadius: '8px',
                boxShadow: '0 18px 42px rgba(15,23,42,0.28)',
                overflow: 'hidden',
              });
              document.body.appendChild(ghost);
              event.dataTransfer.setDragImage(
                ghost,
                Math.min(rect.width - 12, Math.max(12, event.clientX - rect.left)),
                Math.min(rect.height - 8, Math.max(8, event.clientY - rect.top)),
              );
              window.setTimeout(() => ghost.remove(), 0);
            }
            onTraceDragStart(traceKey);
          }}
          onDragEnd={onTraceDragEnd}
          sx={{ display: 'grid', placeItems: 'center', color: chrome.meta, cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
        >
          <DragIndicatorIcon sx={{ fontSize: 17 }} />
        </Box>
        {onAddTrace ? (
          <MuiTooltip title={`Add another channel to ${title}`} placement="top" arrow>
            <IconButton
              size="small"
              aria-label={`Add another channel to ${title}`}
              onClick={(event) => onAddTrace(event.currentTarget)}
              sx={{
                width: 22,
                height: 22,
                p: 0,
                border: `1px solid ${chrome.chartRowBorder}`,
                color: chrome.meta,
                bgcolor: chrome.panelBg,
                '&:hover': { color: '#2563EB', bgcolor: 'rgba(37,99,235,0.1)' },
              }}
            >
              <AddIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </MuiTooltip>
        ) : <Box aria-hidden sx={{ width: 22, height: 22, flexShrink: 0 }} />}
        <MuiTooltip title={`Remove ${title}`} placement="top" arrow>
          <IconButton
            size="small"
            aria-label={`Remove ${title}`}
            onClick={onRemove}
            sx={{
              width: 22,
              height: 22,
              p: 0,
              border: `1px solid ${chrome.chartRowBorder}`,
              color: chrome.meta,
              bgcolor: chrome.panelBg,
              '&:hover': { color: '#DC2626', bgcolor: 'rgba(220,38,38,0.08)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </MuiTooltip>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 750, color: chrome.title }}>{title}</Typography>
          {subtitle ? (
            <Typography noWrap sx={{ fontSize: '0.68rem', color: chrome.chartSubtitle, lineHeight: 1.2 }}>{subtitle}</Typography>
          ) : null}
        </Box>
      </Stack>
      <Box sx={{ position: 'relative', height: chartHeight, minWidth: 0 }}>
        {legendItems.length > 0 ? (
          <Stack
            direction="row"
            sx={{
              position: 'absolute',
              top: 2,
              right: 5,
              zIndex: 5,
              maxWidth: 'calc(100% - 10px)',
              px: 0.55,
              py: 0.25,
              borderRadius: '5px',
              bgcolor: chrome.tooltipBg,
              border: `1px solid ${chrome.tooltipBorder}`,
              pointerEvents: 'none',
              overflow: 'hidden',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 0.8,
            }}
          >
            {legendItems.map((item) => (
              <Stack key={`${item.label}:${item.color}`} direction="row" spacing={0.4} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Box component="svg" viewBox="0 0 14 4" sx={{ width: 14, height: 4, flexShrink: 0 }}>
                  <Box
                    component="line"
                    x1="0"
                    y1="2"
                    x2="14"
                    y2="2"
                    stroke={item.color}
                    strokeWidth="2.5"
                    strokeDasharray={item.dashPattern}
                  />
                </Box>
                <Typography noWrap sx={{ maxWidth: 92, fontSize: '0.6rem', lineHeight: 1.2, fontWeight: 800, color: chrome.title }}>
                  {item.label}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : null}
        {children}
      </Box>
      <Stack direction="row" spacing={0.4} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
        <Typography
          sx={{
            minWidth: 0,
            fontSize: '0.84rem',
            color: chrome.liveValue,
            fontWeight: 850,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
          }}
        >
          {liveValue ?? '—'}
        </Typography>
      </Stack>
      <Box
        role="separator"
        aria-label={`Resize ${title} graph`}
        aria-orientation="horizontal"
        aria-valuemin={MIN_TRACE_GRAPH_HEIGHT}
        aria-valuemax={maximumChartHeight}
        aria-valuenow={chartHeight}
        aria-valuetext={`${chartHeight} pixels high`}
        tabIndex={0}
        title="Drag to resize. Double-click or press Home to reset."
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          resizeStartRef.current = { clientY: event.clientY, chartHeight };
          event.currentTarget.setPointerCapture(event.pointerId);
          setResizing(true);
        }}
        onPointerMove={(event) => {
          if (!resizing) return;
          event.preventDefault();
          event.stopPropagation();
          setClampedChartHeight(
            resizeStartRef.current.chartHeight + event.clientY - resizeStartRef.current.clientY,
          );
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          setResizing(false);
        }}
        onPointerCancel={() => setResizing(false)}
        onDoubleClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onChartHeightReset();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Home') {
            event.preventDefault();
            onChartHeightReset();
            return;
          }
          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
          event.preventDefault();
          const step = event.shiftKey ? 32 : 12;
          setClampedChartHeight(chartHeight + (event.key === 'ArrowDown' ? step : -step));
        }}
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: -1,
          zIndex: 12,
          display: 'grid',
          placeItems: 'center',
          width: 64,
          height: 14,
          borderRadius: '7px 7px 0 0',
          color: resizing ? '#7C3AED' : chrome.meta,
          bgcolor: resizing ? 'rgba(124,58,237,0.14)' : chrome.panelBg,
          border: `1px solid ${resizing ? '#8B5CF6' : chrome.chartRowBorder}`,
          borderBottom: 0,
          cursor: 'ns-resize',
          transform: 'translateX(-50%)',
          touchAction: 'none',
          transition: 'color 120ms ease, background-color 120ms ease, border-color 120ms ease',
          '&:hover, &:focus-visible': {
            color: '#7C3AED',
            bgcolor: 'rgba(124,58,237,0.1)',
            outline: 'none',
          },
        }}
      >
        <DragHandleIcon sx={{ fontSize: 18 }} />
      </Box>
    </Box>
  );
}

function TraceCursor({
  elapsedSec,
  viewStartSec,
  viewEndSec,
  label,
  subtle = false,
  chrome,
}: {
  elapsedSec: number;
  viewStartSec: number;
  viewEndSec: number;
  label?: string | null;
  subtle?: boolean;
  chrome: ReturnType<typeof getTraceChrome>;
}) {
  const viewSpanSec = viewEndSec - viewStartSec;
  if (viewSpanSec <= 0 || elapsedSec < viewStartSec || elapsedSec > viewEndSec) return null;
  const leftPct = viewSpanSec > 0
    ? Math.min(100, Math.max(0, ((elapsedSec - viewStartSec) / viewSpanSec) * 100))
    : 0;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 4,
        bottom: 0,
        left: `${leftPct}%`,
        width: 0,
        borderLeft: subtle
          ? `1px solid ${chrome.cursorHoverLine}`
          : `1.2px solid ${chrome.cursorLine}`,
        pointerEvents: 'none',
        transform: 'translateX(-0.6px)',
        zIndex: subtle ? 9 : 10,
      }}
    >
      {label ? (
        <Box
          sx={{
            position: 'absolute',
            top: -2,
            left: leftPct > 72 ? 'auto' : 6,
            right: leftPct > 72 ? 6 : 'auto',
            px: 0.65,
            py: 0.35,
            borderRadius: '4px',
            bgcolor: chrome.cursorLabelBg,
            border: `1px solid ${chrome.cursorLabelBorder}`,
            color: chrome.cursorLabelText,
            fontSize: '0.76rem',
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            transform: leftPct > 72 ? 'translateX(0)' : 'translateX(0)',
            zIndex: 11,
          }}
        >
          {label}
        </Box>
      ) : null}
    </Box>
  );
}

export function SessionTraceTimeline({
  windows,
  gps,
  sourceLabel,
  selectedLapNumber,
  currentLapStartMs,
  currentLapDurationSec,
  cursorSec,
  viewStartSec,
  viewEndSec,
  onSeekSec,
  onSelectLap,
  chrome,
}: {
  windows: TraceSessionLapWindow[];
  gps: Array<{ timecodeMs: number; lat: number; lon: number }>;
  sourceLabel?: string;
  selectedLapNumber: number;
  currentLapStartMs: number;
  currentLapDurationSec: number;
  cursorSec: number;
  viewStartSec: number;
  viewEndSec: number;
  onSeekSec: (elapsedSec: number) => void;
  onSelectLap?: (lapNumber: number) => void;
  chrome: ReturnType<typeof getTraceChrome>;
}) {
  const timeline = useMemo(() => buildTraceSessionTimeline(windows), [windows]);
  const gpsCoordinateLine = useMemo(
    () => timeline ? buildTraceSessionGpsCoordinateLine(gps, timeline) : null,
    [gps, timeline],
  );
  const [storyboardHeight, setStoryboardHeight] = useState(readSessionStoryboardHeight);
  const [resizing, setResizing] = useState(false);
  const resizeStartRef = useRef({ clientY: 0, height: storyboardHeight });
  const updateStoryboardHeight = useCallback((height: number) => {
    const next = clampSessionStoryboardHeight(height);
    setStoryboardHeight(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SESSION_STORYBOARD_HEIGHT_STORAGE_KEY, String(next));
    }
  }, []);
  if (!timeline) return null;

  const selectedLap = timeline.laps.find((lap) => lap.lapNumber === selectedLapNumber) ?? null;
  const currentLapStartSec = Math.min(
    timeline.durationSec,
    Math.max(0, (currentLapStartMs - timeline.startMs) / 1000),
  );
  const currentLapEndSec = Math.min(
    timeline.durationSec,
    currentLapStartSec + Math.max(0, currentLapDurationSec),
  );
  const playheadSec = Math.min(
    currentLapEndSec,
    Math.max(0, currentLapStartSec + cursorSec),
  );
  const viewportStartSec = Math.min(
    currentLapEndSec,
    Math.max(0, currentLapStartSec + viewStartSec),
  );
  const viewportEndSec = Math.min(
    currentLapEndSec,
    Math.max(viewportStartSec, currentLapStartSec + viewEndSec),
  );
  const percent = (seconds: number) =>
    timeline.durationSec > 0
      ? Math.min(100, Math.max(0, (seconds / timeline.durationSec) * 100))
      : 0;

  return (
    <Box
      data-testid="session-trace-timeline"
      aria-label="Session timeline"
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: storyboardHeight,
        minHeight: MIN_SESSION_STORYBOARD_HEIGHT,
        px: 1.5,
        pt: 0,
        pb: 0.8,
        bgcolor: chrome.panelBg,
      }}
    >
      <Box
        role="separator"
        aria-label="Resize session storyboard"
        aria-orientation="horizontal"
        aria-valuemin={MIN_SESSION_STORYBOARD_HEIGHT}
        aria-valuemax={MAX_SESSION_STORYBOARD_HEIGHT}
        aria-valuenow={storyboardHeight}
        aria-valuetext={`${storyboardHeight} pixels high`}
        tabIndex={0}
        title="Drag up to expand. Double-click or press Home to reset."
        onPointerDown={(event) => {
          event.preventDefault();
          resizeStartRef.current = { clientY: event.clientY, height: storyboardHeight };
          event.currentTarget.setPointerCapture(event.pointerId);
          setResizing(true);
        }}
        onPointerMove={(event) => {
          if (!resizing) return;
          event.preventDefault();
          updateStoryboardHeight(
            resizeStartRef.current.height + resizeStartRef.current.clientY - event.clientY,
          );
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          setResizing(false);
        }}
        onPointerCancel={() => setResizing(false)}
        onDoubleClick={() => updateStoryboardHeight(DEFAULT_SESSION_STORYBOARD_HEIGHT)}
        onKeyDown={(event) => {
          if (event.key === 'Home') {
            event.preventDefault();
            updateStoryboardHeight(DEFAULT_SESSION_STORYBOARD_HEIGHT);
            return;
          }
          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
          event.preventDefault();
          const step = event.shiftKey ? 32 : 12;
          updateStoryboardHeight(
            storyboardHeight + (event.key === 'ArrowUp' ? step : -step),
          );
        }}
        sx={{
          height: 10,
          mx: -1.5,
          mb: 0.2,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          cursor: 'row-resize',
          touchAction: 'none',
          borderTop: `1px solid ${chrome.chartRowBorder}`,
          color: chrome.meta,
          bgcolor: resizing ? 'rgba(139,92,246,0.1)' : 'transparent',
          '&:hover, &:focus-visible': {
            color: '#8B5CF6',
            bgcolor: 'rgba(139,92,246,0.08)',
            outline: 'none',
          },
        }}
      >
        <DragHandleIcon sx={{ width: 18, height: 12 }} />
      </Box>
      <Stack direction="row" sx={{ minHeight: 18, alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            color: chrome.sectionLabel,
            fontSize: '0.64rem',
            fontWeight: 850,
            letterSpacing: '0.055em',
          }}
        >
          {gpsCoordinateLine ? 'GPS POSITION' : 'SESSION'} · {sourceLabel ? `${sourceLabel} · ` : ''}{formatTraceClock(timeline.durationSec)}
        </Typography>
        <Typography
          sx={{
            color: chrome.meta,
            fontSize: '0.64rem',
            fontWeight: 750,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {selectedLap ? `${selectedLap.label} · ` : ''}{formatTraceClock(cursorSec)}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 34,
          mt: 0.25,
          overflow: 'hidden',
          border: `1px solid ${chrome.chartRowBorder}`,
          borderRadius: '6px',
          bgcolor: chrome.shellBg,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 4,
            height: 1,
            bgcolor: chrome.gridStroke,
            pointerEvents: 'none',
          }}
        />

        {timeline.laps.map((lap) => {
          const left = percent(lap.startSec);
          const width = Math.max(0.35, percent(lap.endSec) - left);
          const selected = lap.lapNumber === selectedLapNumber;
          const interactive = selected || (lap.selectable && Boolean(onSelectLap));

          return (
            <MuiTooltip
              key={`${lap.lapNumber}:${lap.startMs}:${lap.endMs}`}
              title={`${lap.label} · ${formatTraceClock(lap.durationSec)}`}
              arrow
            >
              <Box
                component="button"
                type="button"
                aria-label={`${lap.label}, ${formatTraceClock(lap.durationSec)}${selected ? ', selected' : ''}`}
                aria-current={selected ? 'true' : undefined}
                aria-disabled={!interactive}
                tabIndex={interactive ? 0 : -1}
                onClick={(event) => {
                  if (!interactive) return;
                  if (!selected) {
                    onSelectLap?.(lap.lapNumber);
                    return;
                  }

                  const rect = event.currentTarget.getBoundingClientRect();
                  const ratio = event.detail === 0 || rect.width <= 0
                    ? 0
                    : Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
                  onSeekSec(ratio * currentLapDurationSec);
                }}
                sx={{
                  position: 'absolute',
                  insetBlock: 0,
                  left: `${left}%`,
                  width: `${width}%`,
                  m: 0,
                  p: 0,
                  overflow: 'hidden',
                  border: 0,
                  borderRadius: 0,
                  color: selected ? chrome.title : chrome.meta,
                  bgcolor: selected ? 'rgba(124,58,237,0.14)' : 'transparent',
                  cursor: interactive ? 'pointer' : 'default',
                  font: 'inherit',
                  textAlign: 'center',
                  transition: 'background-color 120ms ease',
                  zIndex: 0,
                  '&:hover': interactive
                    ? { bgcolor: selected ? 'rgba(124,58,237,0.2)' : 'rgba(148,163,184,0.1)' }
                    : undefined,
                  '&:focus-visible': {
                    zIndex: 4,
                    outline: '2px solid #8B5CF6',
                    outlineOffset: -2,
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    display: 'block',
                    mt: 0.3,
                    px: 0.3,
                    overflow: 'hidden',
                    color: 'inherit',
                    fontSize: '0.6rem',
                    fontWeight: selected ? 900 : 750,
                    lineHeight: 1.2,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lap.label}
                </Typography>
              </Box>
            </MuiTooltip>
          );
        })}

        {timeline.laps.slice(0, -1).map((lap) => (
          <Box
            key={`marker:${lap.lapNumber}:${lap.endMs}`}
            data-testid="session-lap-marker"
            aria-hidden="true"
            sx={{
              position: 'absolute',
              insetBlock: 0,
              left: `${percent(lap.endSec)}%`,
              zIndex: 2,
              width: 0,
              borderLeft: `1px solid ${chrome.cursorHoverLine}`,
              pointerEvents: 'none',
            }}
          />
        ))}

        {gpsCoordinateLine ? (
          <Box
            component="svg"
            data-testid="session-gps-coordinate-line"
            aria-label="GPS coordinate overview"
            role="img"
            viewBox="0 0 1000 100"
            preserveAspectRatio="none"
            sx={{
              position: 'absolute',
              top: 13,
              right: 0,
              bottom: 2,
              left: 0,
              zIndex: 1,
              width: '100%',
              height: 'auto',
              overflow: 'visible',
              pointerEvents: 'none',
            }}
          >
            <polyline
              points={gpsCoordinateLine.points.map((point) => `${point.x},${point.y}`).join(' ')}
              fill="none"
              stroke="#8B5CF6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </Box>
        ) : null}

        <Box
          data-testid="session-trace-viewport"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            top: 13,
            bottom: 2,
            left: `${percent(viewportStartSec)}%`,
            zIndex: 3,
            width: `${Math.max(0.35, percent(viewportEndSec) - percent(viewportStartSec))}%`,
            height: 'auto',
            borderLeft: '2px solid #8B5CF6',
            borderRight: '2px solid #8B5CF6',
            bgcolor: 'rgba(139,92,246,0.08)',
            boxShadow: 'inset 0 1px 0 rgba(139,92,246,0.65), inset 0 -1px 0 rgba(139,92,246,0.65)',
            pointerEvents: 'none',
          }}
        />

        <Box
          data-testid="session-trace-playhead"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            insetBlock: 2,
            left: `${percent(playheadSec)}%`,
            zIndex: 4,
            width: 0,
            borderLeft: `2px solid ${chrome.cursorLine}`,
            pointerEvents: 'none',
            transform: 'translateX(-1px)',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: -1,
              left: -4,
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: chrome.cursorLine,
            },
          }}
        />
      </Box>
    </Box>
  );
}

function InteractiveTraceFrame({
  cursorSec,
  viewStartSec,
  viewEndSec,
  onSeekSec,
  labelForSec,
  children,
  chrome,
  selectedRange,
  onSelectRange,
  onZoomAtSec,
}: {
  cursorSec: number;
  viewStartSec: number;
  viewEndSec: number;
  onSeekSec: (elapsedSec: number) => void;
  labelForSec: (elapsedSec: number) => string | null;
  children: ReactNode;
  chrome: ReturnType<typeof getTraceChrome>;
  selectedRange?: { startSec: number; endSec: number } | null;
  onSelectRange?: (startSec: number, endSec: number) => void;
  onZoomAtSec?: (elapsedSec: number, direction: 'in' | 'out') => void;
}) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const wheelDeltaRef = useRef(0);
  const lastWheelZoomAtRef = useRef(0);
  const [hoverSec, setHoverSec] = useState<number | null>(null);
  const [dragStartSec, setDragStartSec] = useState<number | null>(null);
  const [dragCurrentSec, setDragCurrentSec] = useState<number | null>(null);
  const viewSpanSec = Math.max(0, viewEndSec - viewStartSec);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !onZoomAtSec) return undefined;

    const handleWheel = (event: WheelEvent) => {
      if (!Number.isFinite(event.deltaY) || event.deltaY === 0) return;
      if (!isTraceZoomWheelGesture(event)) {
        wheelDeltaRef.current = 0;
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      const deltaMultiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? frame.clientHeight
          : 1;
      const normalizedDelta = event.deltaY * deltaMultiplier;
      if (
        wheelDeltaRef.current !== 0
        && Math.sign(wheelDeltaRef.current) !== Math.sign(normalizedDelta)
      ) {
        wheelDeltaRef.current = 0;
      }
      wheelDeltaRef.current += normalizedDelta;

      const threshold = event.ctrlKey ? 8 : 36;
      const now = performance.now();
      if (
        Math.abs(wheelDeltaRef.current) < threshold
        || now - lastWheelZoomAtRef.current < 90
      ) {
        return;
      }

      const rect = frame.getBoundingClientRect();
      const ratio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0.5;
      const anchorSec = Math.min(
        viewEndSec,
        Math.max(viewStartSec, viewStartSec + Math.min(1, Math.max(0, ratio)) * viewSpanSec),
      );
      const direction = wheelDeltaRef.current < 0 ? 'in' : 'out';
      wheelDeltaRef.current = 0;
      lastWheelZoomAtRef.current = now;
      onZoomAtSec(anchorSec, direction);
    };

    frame.addEventListener('wheel', handleWheel, { passive: false });
    return () => frame.removeEventListener('wheel', handleWheel);
  }, [onZoomAtSec, viewEndSec, viewSpanSec, viewStartSec]);

  const secFromEvent = (event: ReactPointerEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
    return Math.min(viewEndSec, Math.max(viewStartSec, viewStartSec + ratio * viewSpanSec));
  };

  const updateHover = (event: ReactPointerEvent<HTMLElement>) => {
    const nextSec = secFromEvent(event);
    setHoverSec(nextSec);
    if (dragStartSec !== null) setDragCurrentSec(nextSec);
  };

  const activeSelection = dragStartSec !== null && dragCurrentSec !== null
    ? {
        startSec: Math.min(dragStartSec, dragCurrentSec),
        endSec: Math.max(dragStartSec, dragCurrentSec),
      }
    : selectedRange;
  const visibleSelectionStart = activeSelection
    ? Math.max(viewStartSec, activeSelection.startSec)
    : 0;
  const visibleSelectionEnd = activeSelection
    ? Math.min(viewEndSec, activeSelection.endSec)
    : 0;
  const selectionLeft = activeSelection && viewSpanSec > 0
    ? ((visibleSelectionStart - viewStartSec) / viewSpanSec) * 100
    : 0;
  const selectionWidth = activeSelection && viewSpanSec > 0
    ? (Math.max(0, visibleSelectionEnd - visibleSelectionStart) / viewSpanSec) * 100
    : 0;

  return (
    <Box
      ref={frameRef}
      title="Pinch or use a mouse wheel to zoom"
      sx={{ position: 'relative', height: '100%', cursor: 'crosshair', touchAction: 'pan-y' }}
      onPointerEnter={updateHover}
      onPointerMove={updateHover}
      onPointerLeave={() => {
        if (dragStartSec === null) {
          setHoverSec(null);
        }
      }}
      onPointerDown={(event) => {
        const startSec = secFromEvent(event);
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragStartSec(startSec);
        setDragCurrentSec(startSec);
        setHoverSec(startSec);
      }}
      onPointerUp={(event) => {
        const endSec = secFromEvent(event);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (dragStartSec !== null) {
          const startSec = Math.min(dragStartSec, endSec);
          const finishSec = Math.max(dragStartSec, endSec);
          const minimumSelectionSec = Math.max(0.15, viewSpanSec * 0.003);
          if (finishSec - startSec >= minimumSelectionSec && onSelectRange) {
            onSelectRange(startSec, finishSec);
          } else {
            onSeekSec(endSec);
          }
        }
        setDragStartSec(null);
        setDragCurrentSec(null);
        setHoverSec(endSec);
      }}
      onPointerCancel={() => {
        setDragStartSec(null);
        setDragCurrentSec(null);
      }}
    >
      {children}
      {activeSelection && selectionWidth > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            insetBlock: 0,
            left: `${selectionLeft}%`,
            width: `${selectionWidth}%`,
            bgcolor: 'rgba(59,130,246,0.13)',
            borderLeft: '1px solid rgba(59,130,246,0.85)',
            borderRight: '1px solid rgba(59,130,246,0.85)',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />
      ) : null}
      <TraceCursor
        elapsedSec={cursorSec}
        viewStartSec={viewStartSec}
        viewEndSec={viewEndSec}
        label={hoverSec === null ? labelForSec(cursorSec) : null}
        chrome={chrome}
      />
      {hoverSec !== null ? (
        <TraceCursor
          elapsedSec={hoverSec}
          viewStartSec={viewStartSec}
          viewEndSec={viewEndSec}
          label={labelForSec(hoverSec)}
          subtle={dragStartSec === null}
          chrome={chrome}
        />
      ) : null}
    </Box>
  );
}

export function SessionCompareTracePanel({
  layers,
  cursorSec,
  durationSec,
  alignmentMode = 'time',
  onSeekSec,
  drawer = false,
  fillHeight = false,
  tracePickerOpen = false,
  onTracePickerClose,
  saveDialogOpen = false,
  onSaveDialogClose,
  selectedRange = null,
  onSelectRange,
  onReplaySelection,
  onClearSelection,
  onTraceZoomRangeChange,
}: SessionCompareTracePanelProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const chrome = getTraceChrome(isDark);
  const chartTooltipStyle = useMemo(() => ({
    background: chrome.tooltipBg,
    border: `1px solid ${chrome.tooltipBorder}`,
    borderRadius: 8,
    fontSize: 11,
    color: chrome.title,
  }), [chrome]);
  const measurementPreferences = useMeasurementPreferences();
  const primaryLayer = layers.find((layer) => layer.id === 'primary') ?? null;
  const compareLayers = useMemo(() => layers.filter((layer) => layer.id !== 'primary'), [layers]);
  const alignmentMappers = useMemo(
    () => buildTraceAlignmentMappers(layers, alignmentMode),
    [alignmentMode, layers],
  );
  const traceDurationSec = alignmentMode === 'gps' && primaryLayer
    ? primaryLayer.analysis.durationSec
    : durationSec;
  const finishSecForLayer = useCallback(
    (layer: SessionCompareReplayLayer) => (
      alignmentMode === 'gps' && primaryLayer
        ? primaryLayer.analysis.durationSec
        : layer.analysis.durationSec
    ),
    [alignmentMode, primaryLayer],
  );
  const sourceElapsedForLayer = useCallback(
    (layer: SessionCompareReplayLayer, alignedElapsedSec: number) => (
      alignmentMappers.get(layer.id)?.fromAlignedSec(alignedElapsedSec) ?? alignedElapsedSec
    ),
    [alignmentMappers],
  );

  const availableSeriesChannels = useMemo(
    () =>
      primaryLayer
        ? listPopulatedTraceChannels(
            primaryLayer.sessionExport,
            primaryLayer.analysis.startMs,
            primaryLayer.analysis.endMs,
          )
        : [],
    [primaryLayer],
  );

  const allSeriesChannels = useMemo(
    () =>
      primaryLayer
        ? listAllTraceChannels(
            primaryLayer.sessionExport,
            primaryLayer.analysis.startMs,
            primaryLayer.analysis.endMs,
          )
        : [],
    [primaryLayer],
  );

  const traceOptions = useMemo((): TraceOption[] => {
    const channelOptions = allSeriesChannels.map((channel) => ({
      key: channel.shortName,
      label: channel.longName || channel.shortName,
      sublabel: channel.sampleCount === 0
        ? `${channel.shortName} · No data in this lap`
        : `${channel.shortName}${channel.unit ? ` · ${channel.unit}` : ''} · ${channel.sampleCount.toLocaleString()} samples`,
      color: channel.color,
      kind: 'series' as const,
      shortName: channel.shortName,
      unit: channel.unit,
      decimals: channel.decimals,
      sampleCount: channel.sampleCount,
      description: traceChannelDescription(
        channel.shortName,
        channel.longName || channel.shortName,
        channel.unit,
      ),
    }));
    const speedOption: TraceOption[] = primaryLayer && primaryLayer.points.length >= 2
      ? [{
          key: SPEED_TRACE_KEY,
          label: 'Speed',
          sublabel: 'GPS ground speed trace',
          color: '#60A5FA',
          kind: 'ground-speed',
          shortName: 'WSpd',
          unit: 'km/h',
          decimals: 1,
          description: 'GPS-derived ground speed along the driven lap path.',
        }]
      : [];
    const gpsCoordinateSampleCount = primaryLayer
      ? primaryLayer.sessionExport.gps.filter(
          (point) =>
            point.timecodeMs >= primaryLayer.analysis.startMs
            && point.timecodeMs <= primaryLayer.analysis.endMs,
        ).length
      : 0;
    const gpsCoordinateOptions: TraceOption[] = gpsCoordinateSampleCount >= 2
      ? [
          {
            key: GPS_LATITUDE_TRACE_KEY,
            label: 'GPS Latitude',
            sublabel: `North-south coordinate · ${gpsCoordinateSampleCount.toLocaleString()} samples`,
            color: '#22C55E',
            kind: 'gps-coordinate',
            gpsCoordinate: 'lat',
            shortName: 'GPS Lat',
            unit: '°',
            decimals: 6,
            sampleCount: gpsCoordinateSampleCount,
            description: 'Recorded GPS latitude over the selected lap.',
          },
          {
            key: GPS_LONGITUDE_TRACE_KEY,
            label: 'GPS Longitude',
            sublabel: `East-west coordinate · ${gpsCoordinateSampleCount.toLocaleString()} samples`,
            color: '#06B6D4',
            kind: 'gps-coordinate',
            gpsCoordinate: 'lon',
            shortName: 'GPS Lon',
            unit: '°',
            decimals: 6,
            sampleCount: gpsCoordinateSampleCount,
            description: 'Recorded GPS longitude over the selected lap.',
          },
        ]
      : [];
    const histogramOptions: TraceOption[] = [
      ...(primaryLayer && primaryLayer.points.length >= 2
        ? [{
            key: SPEED_HISTOGRAM_TRACE_KEY,
            label: 'Speed Histogram',
            sublabel: 'Time spent in speed ranges',
            color: '#60A5FA',
            kind: 'histogram' as const,
            histogramMetric: 'speed' as const,
            unit: 'km/h',
            decimals: 0,
            description: 'Distribution of lap time across speed ranges.',
          }]
        : []),
      ...(availableSeriesChannels.some((channel) => channel.shortName === 'RPM')
        ? [{
            key: RPM_HISTOGRAM_TRACE_KEY,
            label: 'RPM Histogram',
            sublabel: 'Time spent in RPM bands for gearing',
            color: '#F97316',
            kind: 'histogram' as const,
            histogramMetric: 'rpm' as const,
            unit: 'rpm',
            decimals: 0,
            description: 'Distribution of lap time across engine-speed ranges for gearing analysis.',
          }]
        : []),
    ];

    if (!primaryLayer || compareLayers.length === 0) {
      return [...speedOption, ...gpsCoordinateOptions, ...histogramOptions, ...channelOptions];
    }

    return [
      ...speedOption,
      ...gpsCoordinateOptions,
      ...histogramOptions,
      ...channelOptions,
      {
        key: SPEED_DELTA_TRACE_KEY,
        label: 'Speed Δ',
        sublabel: 'Compare lap speed difference',
        color: compareLayers[0]?.color ?? '#FFB000',
        kind: 'speed-delta',
        description: 'Instantaneous speed difference between the primary lap and each compared lap.',
      },
    ];
  }, [allSeriesChannels, availableSeriesChannels, compareLayers, primaryLayer]);

  const [traceGraphHeights, setTraceGraphHeights] = useState<Record<string, number>>(
    readTraceGraphHeights,
  );
  const [traceZoomRange, setTraceZoomRange] = useState<TraceTimeRange | null>(null);
  useEffect(() => {
    onTraceZoomRangeChange?.(traceZoomRange);
  }, [onTraceZoomRangeChange, traceZoomRange]);
  const [traceSearch, setTraceSearch] = useState('');
  const [expandedTraceGroups, setExpandedTraceGroups] = useState<Set<string>>(
    () => new Set(['Common traces', 'Compare traces']),
  );
  const traceOptionGroups = useMemo(() => {
    const query = traceSearch.trim().toLowerCase();
    const filtered = query
      ? traceOptions.filter((option) => `${option.label} ${option.shortName ?? ''} ${option.sublabel}`.toLowerCase().includes(query))
      : traceOptions;

    return TRACE_GROUP_ORDER.flatMap((label) => {
      const options = filtered
        .filter((option) => traceGroupForOption(option) === label)
        .sort((left, right) => {
          const leftHasData = left.kind !== 'series' || (left.sampleCount ?? 0) > 0;
          const rightHasData = right.kind !== 'series' || (right.sampleCount ?? 0) > 0;
          if (leftHasData !== rightHasData) return leftHasData ? -1 : 1;
          if (label === 'Common traces') {
            const rankDifference = commonTraceRank(left) - commonTraceRank(right);
            if (rankDifference !== 0) return rankDifference;
          }
          return left.label.localeCompare(right.label);
        });
      if (options.length === 0) return [];
      const availableCount = options.filter((option) => option.kind !== 'series' || (option.sampleCount ?? 0) > 0).length;
      return [{ label, options, availableCount }];
    });
  }, [traceOptions, traceSearch]);

  const [enabledTraceKeys, setEnabledTraceKeys] = useState<string[]>([]);
  const [draggedTraceKey, setDraggedTraceKey] = useState<string | null>(null);
  const savedViews = useSavedTraceViews();
  const { client: viewsClient, scope: viewsScope } = savedViews;
  const savedTraceViews = savedViews.items;
  const [loadedTraceView, setLoadedTraceView] = useState<CloudTraceView | null>(null);
  const [legacyViews] = useState(() => readLegacyTraceViews(traceViewBrowserStorage()));
  const [legacyImport, setLegacyImport] = useState<LegacyTraceView | null>(null);
  const [importConsent, setImportConsent] = useState(false);
  const [importScope, setImportScope] = useState('');
  const [importNotice, setImportNotice] = useState('');
  const selectionRequest = useRef(0);
  const pendingCreate = useRef(new PendingTraceViewCreate());
  const previousViewsScope = useRef(viewsScope);
  const [selectedTraceViewId, setSelectedTraceViewId] = useState('');
  const [retryTraceViewId, setRetryTraceViewId] = useState<string | null>(null);
  const [lastTraceViewId] = useState(() => {
    const cloudId = readSelectedCloudTraceView(viewsScope);
    if (cloudId) return cloudId;
    const id = readSessionReviewPreferences().traceViewId;
    return id && isBuiltInTraceViewId(id) ? id : null;
  });
  const pendingTraceViewIdRef = useRef(lastTraceViewId);
  const [traceViewName, setTraceViewName] = useState('');
  const [traceLabels, setTraceLabels] = useState<Record<string, string>>({});
  const [tracePairs, setTracePairs] = useState<Record<string, string>>({});
  const [traceUnits, setTraceUnits] = useState<Record<string, string>>({});
  const [pairMenuAnchor, setPairMenuAnchor] = useState<HTMLElement | null>(null);
  const [pairMenuPrimaryKey, setPairMenuPrimaryKey] = useState<string | null>(null);
  const [pairSearch, setPairSearch] = useState('');
  useEffect(() => {
    if (previousViewsScope.current === viewsScope) return;
    previousViewsScope.current = viewsScope;
    selectionRequest.current += 1;
    pendingCreate.current.clear();
    setSelectedTraceViewId('');
    setRetryTraceViewId(null);
    setLoadedTraceView(null);
    setTraceViewName('');
    setTraceLabels({});
    setTracePairs({});
    setTraceUnits({});
    setEnabledTraceKeys([]);
    setLegacyImport(null);
    setImportConsent(false);
    setImportNotice('');
    pendingTraceViewIdRef.current = readSelectedCloudTraceView(viewsScope);
  }, [viewsScope]);


  useEffect(() => {
    setTraceZoomRange(null);
  }, [alignmentMode, traceDurationSec, primaryLayer?.id]);
  const speedDeltaWasAvailableRef = useRef(false);

  useEffect(() => {
    const speedDeltaAvailable = traceOptions.some((option) => option.key === SPEED_DELTA_TRACE_KEY);
    const speedDeltaWasAvailable = speedDeltaWasAvailableRef.current;
    speedDeltaWasAvailableRef.current = speedDeltaAvailable;

    if (selectedTraceViewId && !isBuiltInTraceViewId(selectedTraceViewId)) return;
    const available = new Set(traceOptions.map((option) => option.key));
    const valid = enabledTraceKeys.filter((key) => available.has(key));
    let next: string[];

    if (valid.length > 0) {
      next = valid;
      if (
        speedDeltaAvailable
        && !speedDeltaWasAvailable
        && !next.includes(SPEED_DELTA_TRACE_KEY)
      ) {
        next = [...next, SPEED_DELTA_TRACE_KEY];
      }
    } else {
      const defaults = defaultEnabledTraceChannels(availableSeriesChannels, true);
      if (available.has(SPEED_TRACE_KEY)) {
        next = [SPEED_TRACE_KEY];
      } else if (defaults.includes('RPM') && available.has('RPM')) {
        next = ['RPM'];
      } else if (defaults.length > 0) {
        next = defaults.slice(0, 1);
      } else {
        next = traceOptions[0] ? [traceOptions[0].key] : [];
      }
      if (available.has(SPEED_DELTA_TRACE_KEY)) {
        next.push(SPEED_DELTA_TRACE_KEY);
      }
      next = next.filter((key) => available.has(key));
    }

    if (!sameTraceKeys(enabledTraceKeys, next)) {
      setEnabledTraceKeys(next);
      setTracePairs((current) => retainSelectedTracePairs(next, current));
    }
  }, [availableSeriesChannels, enabledTraceKeys, traceOptions, selectedTraceViewId]);

  const enabledTraceOptions = useMemo(
    () => enabledTraceKeys.flatMap((key) => {
      const option = traceOptions.find((entry) => entry.key === key);
      return option ? [option] : [];
    }),
    [enabledTraceKeys, traceOptions],
  );
  const pairMenuPrimary = pairMenuPrimaryKey
    ? traceOptions.find((option) => option.key === pairMenuPrimaryKey) ?? null
    : null;
  const pairMenuOptions = useMemo(() => {
    const query = pairSearch.trim().toLowerCase();
    const primaryWords = new Set(
      (pairMenuPrimary?.label ?? '').toLowerCase().split(/\W+/).filter((word) => word.length > 2),
    );
    return traceOptions
      .filter((option) => (
        option.key !== pairMenuPrimaryKey
        && traceOptionSupportsPair(option)
        && (!query || `${option.label} ${option.shortName ?? ''} ${option.sublabel}`.toLowerCase().includes(query))
      ))
      .sort((left, right) => {
        const related = (option: TraceOption) => option.label
          .toLowerCase()
          .split(/\W+/)
          .filter((word) => primaryWords.has(word)).length;
        const relationDifference = related(right) - related(left);
        if (relationDifference !== 0) return relationDifference;
        const dataDifference = Number((right.sampleCount ?? 1) > 0) - Number((left.sampleCount ?? 1) > 0);
        return dataDifference || left.label.localeCompare(right.label);
      });
  }, [pairMenuPrimary?.label, pairMenuPrimaryKey, pairSearch, traceOptions]);

  const seriesTraceData = useMemo(
    () =>
      Object.fromEntries(
        enabledTraceOptions
          .filter((option) => option.kind === 'series' && option.shortName)
          .map((option) => [
            option.key,
            mergeLayerTraces(layers, option.shortName!, alignmentMappers),
          ] as const),
      ),
    [alignmentMappers, enabledTraceOptions, layers],
  );

  const gpsCoordinateTraceData = useMemo(
    () => Object.fromEntries(
      enabledTraceOptions
        .filter((option) => option.kind === 'gps-coordinate' && option.gpsCoordinate)
        .map((option) => [
          option.key,
          mergeGpsCoordinateTraces(layers, option.gpsCoordinate!, alignmentMappers),
        ] as const),
    ),
    [alignmentMappers, enabledTraceOptions, layers],
  );

  const groundSpeedTrace = useMemo(
    () => enabledTraceKeys.includes(SPEED_TRACE_KEY)
      ? mergeGroundSpeedTraces(layers, alignmentMappers)
      : [],
    [alignmentMappers, enabledTraceKeys, layers],
  );

  const histogramTraceData = useMemo(() => ({
    speed: enabledTraceKeys.includes(SPEED_HISTOGRAM_TRACE_KEY) ? buildHistogramRows(layers, 'speed', measurementPreferences) : [],
    rpm: enabledTraceKeys.includes(RPM_HISTOGRAM_TRACE_KEY) ? buildHistogramRows(layers, 'rpm', measurementPreferences) : [],
  }), [enabledTraceKeys, layers, measurementPreferences]);

  const speedDeltaTraces = useMemo(() => {
    if (!primaryLayer || compareLayers.length === 0) {
      return [];
    }

    return compareLayers.map((layer) => ({
      layer,
      data: buildSpeedDeltaTrace(
        primaryLayer,
        layer,
        (elapsedSec) => sourceElapsedForLayer(layer, elapsedSec),
      ),
    }));
  }, [compareLayers, primaryLayer, sourceElapsedForLayer]);

  const primaryCursorTimeMs = primaryLayer
    ? primaryLayer.analysis.startMs + cursorSec * 1000
    : null;

  const liveSeriesValuesByTrace = useMemo(() => {
    if (primaryCursorTimeMs === null) {
      return new Map<string, Map<string, number | null>>();
    }

    return new Map(
      enabledTraceOptions
        .filter((option) => option.kind === 'series' && option.shortName)
        .map((option) => [
          option.key,
          new Map(
            layers.map((layer) => {
              const sourceElapsedSec = sourceElapsedForLayer(layer, cursorSec);
              const timecodeMs = layer.analysis.startMs + sourceElapsedSec * 1000;
              if (timecodeMs < layer.analysis.startMs || timecodeMs > layer.analysis.endMs) return [layer.id, null];
              const value = seriesValueAtTime(
                layer.sessionExport,
                option.shortName!,
                timecodeMs,
                layer.analysis.accXBaseline ?? null,
              );
              return [layer.id, value];
            }),
          ),
        ]),
    );
  }, [cursorSec, enabledTraceOptions, layers, primaryCursorTimeMs, sourceElapsedForLayer]);

  const liveGroundSpeedValues = useMemo(() => {
    if (!enabledTraceKeys.includes(SPEED_TRACE_KEY)) {
      return new Map<string, number | null>();
    }

    return new Map(layers.map((layer) => [
      layer.id,
      interpolatedLayerGroundSpeed(layer, sourceElapsedForLayer(layer, cursorSec)),
    ]));
  }, [cursorSec, enabledTraceKeys, layers, sourceElapsedForLayer]);

  const liveGpsCoordinateValuesByTrace = useMemo(() => new Map(
    enabledTraceOptions
      .filter((option) => option.kind === 'gps-coordinate' && option.gpsCoordinate)
      .map((option) => [
        option.key,
        new Map(layers.map((layer) => {
          const sourceElapsedSec = sourceElapsedForLayer(layer, cursorSec);
          if (sourceElapsedSec < 0 || sourceElapsedSec > layer.analysis.durationSec) {
            return [layer.id, null] as const;
          }
          const value = interpolatedTraceValue(
            gpsCoordinateTrace(layer, option.gpsCoordinate!),
            sourceElapsedSec,
          );
          return [layer.id, value] as const;
        })),
      ] as const),
  ), [cursorSec, enabledTraceOptions, layers, sourceElapsedForLayer]);

  const liveSpeedDeltas = useMemo(() => {
    if (!primaryLayer || primaryCursorTimeMs === null) {
      return [];
    }

    const primaryKph = layerSpeedKphAtElapsed(primaryLayer, cursorSec);

    return compareLayers.map((layer) => {
      const compareKph = layerSpeedKphAtElapsed(
        layer,
        sourceElapsedForLayer(layer, cursorSec),
      );
      const deltaKph =
        primaryKph !== null && compareKph !== null ? compareKph - primaryKph : null;

      return { layer, deltaKph };
    });
  }, [compareLayers, cursorSec, primaryCursorTimeMs, primaryLayer, sourceElapsedForLayer]);

  const pairedTraceData = useMemo(() => {
    if (!primaryLayer) return {};

    const traceForOption = (
      option: TraceOption,
      layer: SessionCompareReplayLayer,
    ): SeriesTracePoint[] => {
      if (option.kind === 'ground-speed') {
        return alignTracePoints(layer.points.map((point, index) => ({
          index,
          elapsedSec: Math.max(0, (point.timecodeMs - layer.analysis.startMs) / 1000),
          timecodeMs: point.timecodeMs,
          value: point.speedKph,
        })), alignmentMappers.get(layer.id));
      }
      if (option.kind === 'gps-coordinate' && option.gpsCoordinate) {
        return alignTracePoints(
          gpsCoordinateTrace(layer, option.gpsCoordinate),
          alignmentMappers.get(layer.id),
        );
      }
      if (option.kind !== 'series' || !option.shortName) return [];
      return alignTracePoints(buildGenericSeriesTrace(
        layer.sessionExport,
        option.shortName,
        layer.analysis.startMs,
        layer.analysis.endMs,
        layer.analysis.startMs,
      ), alignmentMappers.get(layer.id));
    };

    return Object.fromEntries(Object.entries(tracePairs).flatMap(([primaryKey, secondaryKey]) => {
      const primaryOption = traceOptions.find((option) => option.key === primaryKey);
      const secondaryOption = traceOptions.find((option) => option.key === secondaryKey);
      if (!primaryOption || !secondaryOption) return [];
      const layerTraces = layers.map((layer) => ({
        layer,
        primary: traceForOption(primaryOption, layer),
        secondary: traceForOption(secondaryOption, layer),
      }));
      const rows = mergeSampledTraces(layerTraces.flatMap((entry) => [
        { id: `primary:${entry.layer.id}`, points: entry.primary },
        { id: `secondary:${entry.layer.id}`, points: entry.secondary },
      ]));
      return [[primaryKey, {
        secondaryKey,
        rows,
        primaryDomain: autoSeriesTraceDomain(
          layerTraces.flatMap((entry) => entry.primary),
          primaryOption.shortName ?? 'WSpd',
        ),
        secondaryDomain: autoSeriesTraceDomain(
          layerTraces.flatMap((entry) => entry.secondary),
          secondaryOption.shortName ?? 'WSpd',
        ),
      }]];
    }));
  }, [alignmentMappers, layers, primaryLayer, traceOptions, tracePairs]);

  const pairedSecondaryKeys = useMemo(() => new Set(Object.values(tracePairs)), [tracePairs]);

  const handleChartClick = useCallback((elapsedSec: number) => {
    if (!Number.isFinite(elapsedSec)) {
      return;
    }

    onSeekSec(Math.min(traceDurationSec, Math.max(0, elapsedSec)));
  }, [onSeekSec, traceDurationSec]);

  const handleTraceZoom = useCallback((elapsedSec: number, direction: 'in' | 'out') => {
    setTraceZoomRange((current) => (
      nextTraceZoomRange(current, traceDurationSec, elapsedSec, direction)
    ));
  }, [traceDurationSec]);

  const seriesLabelForSec = useCallback((trace: MergedTraceRow[], option: TraceOption, elapsedSec: number) => {
    if (trace.length === 0 || !option.shortName) {
      return formatTraceClock(elapsedSec);
    }

    const values = layers.flatMap((layer) => {
      const value = interpolatedRowValue(trace, elapsedSec, layer.id);
      return typeof value === 'number' && Number.isFinite(value)
        ? [`${layer.label}: ${formatSeriesTraceValue(
            option.shortName!,
            value,
            option.unit ?? '',
            option.decimals ?? 2,
            measurementPreferences,
          )}`]
        : [];
    });

    return values.length ? `${formatTraceClock(elapsedSec)} | ${values.join(' | ')}` : formatTraceClock(elapsedSec);
  }, [layers, measurementPreferences]);

  function toggleTrace(key: string) {
    pendingTraceViewIdRef.current = null;
    if (enabledTraceKeys.includes(key)) {
      setTracePairs((current) => Object.fromEntries(
        Object.entries(current).filter(([primary, secondary]) => primary !== key && secondary !== key),
      ));
    }
    setEnabledTraceKeys((current) =>
      current.includes(key)
        ? current.filter((entry) => entry !== key)
        : [...current, key],
    );
  }

  function removeTrace(key: string) {
    pendingTraceViewIdRef.current = null;
    const secondaryKey = tracePairs[key];
    const removed = new Set([key, ...(secondaryKey ? [secondaryKey] : [])]);
    setEnabledTraceKeys((current) => current.filter((entry) => !removed.has(entry)));
    setTracePairs((current) => Object.fromEntries(
      Object.entries(current).filter(
        ([primary, secondary]) => !removed.has(primary) && !removed.has(secondary),
      ),
    ));
  }

  function addTraceToGraph(secondaryKey: string) {
    if (!pairMenuPrimaryKey || secondaryKey === pairMenuPrimaryKey || !enabledTraceKeys.includes(pairMenuPrimaryKey)) return;
    pendingTraceViewIdRef.current = null;
    const primaryKey = pairMenuPrimaryKey;
    setEnabledTraceKeys((current) => {
      const withoutSecondary = current.filter((key) => key !== secondaryKey);
      const primaryIndex = withoutSecondary.indexOf(primaryKey);
      if (primaryIndex < 0) return current;
      withoutSecondary.splice(primaryIndex + 1, 0, secondaryKey);
      return withoutSecondary;
    });
    setTracePairs((current) => ({
      ...Object.fromEntries(
        Object.entries(current).filter(
          ([primary, secondary]) => (
            primary !== primaryKey
            && secondary !== primaryKey
            && primary !== secondaryKey
            && secondary !== secondaryKey
          ),
        ),
      ),
      [primaryKey]: secondaryKey,
    }));
    setPairMenuAnchor(null);
    setPairMenuPrimaryKey(null);
    setPairSearch('');
  }

  const selectTraceView = useCallback(async (id: string) => {
    const request = ++selectionRequest.current;
    setRetryTraceViewId(null);
    if (id && !isBuiltInTraceViewId(id)) {
      const view = await viewsClient.get(id);
      if (request !== selectionRequest.current) return;
      if (!view) { setRetryTraceViewId(id); return; }
      pendingCreate.current.clear();
      pendingTraceViewIdRef.current = null;
      setSelectedTraceViewId(view.id);
      setLoadedTraceView(view);
      setTraceViewName(view.name);
      setEnabledTraceKeys(view.traceKeys);
      setTraceLabels(view.labels ?? {});
      setTracePairs(view.pairs ?? {});
      setTraceUnits(view.units ?? {});
      writeSelectedCloudTraceView(viewsScope, view.id);
      return;
    }
    pendingCreate.current.clear();
    setLoadedTraceView(null);
    setTraceUnits({});
    pendingTraceViewIdRef.current = id || null;
    setSelectedTraceViewId(id);
    writeSelectedCloudTraceView(viewsScope, null);
    updateSessionReviewPreferences({ traceViewId: id || null });
    if (!id) {
      setTraceViewName('');
      setTraceLabels({});
      setTracePairs({});
      return;
    }

    const builtInPreset = BUILT_IN_ENGINE_TRACE_PRESETS.find((preset) => preset.id === id);
    if (builtInPreset) {
      const available = new Map(traceOptions.map((option) => [option.key, option]));
      const traceKeys: string[] = [];
      const add = (key: string) => {
        if (available.has(key) && !traceKeys.includes(key)) traceKeys.push(key);
      };
      if (available.has(SPEED_TRACE_KEY)) add(SPEED_TRACE_KEY);
      else add('WSpd');
      add('RPM');
      add('AccX');
      add('AccY');
      add('StrA');

      const lambdaKeys = traceOptions
        .filter((option) => (
          option.kind === 'series'
          && /(?:lambda|\blcu\b|air.?fuel|\bafr\b|oxygen|\bo2\b)/i.test(
            `${option.key} ${option.label} ${option.shortName ?? ''}`,
          )
        ))
        .map((option) => option.key);
      lambdaKeys.forEach(add);
      const engineSensorKeys = traceOptions
        .filter((option) => (
          option.kind === 'series'
          && /(?:\begt\b|exhaust.*temp|\bcht\b|cylinder.*head|coolant|water.*temp|engine.*temp|\btc\s*\d+\b|throttle|\btps\b)/i.test(
            `${option.key} ${option.label} ${option.shortName ?? ''}`,
          )
        ))
        .map((option) => option.key);
      engineSensorKeys.forEach(add);

      const pairs: Record<string, string> = {};
      const addPair = (primary: string, secondary: string) => {
        // A channel can match both lambda and temperature naming rules.
        if (hasValidSavedTracePairs(traceKeys, { ...pairs, [primary]: secondary })) pairs[primary] = secondary;
      };
      if (traceKeys.includes('AccX') && traceKeys.includes('AccY')) addPair('AccX', 'AccY');
      if (lambdaKeys.length >= 2) addPair(lambdaKeys[0]!, lambdaKeys[1]!);
      const temperatureKeys = engineSensorKeys.filter((key) => (
        /(?:egt|temp|cht|tc\s*\d)/i.test(
          `${key} ${available.get(key)?.label ?? ''}`,
        )
      ));
      if (temperatureKeys.length >= 2) addPair(temperatureKeys[0]!, temperatureKeys[1]!);

      setTraceViewName(builtInPreset.name);
      setEnabledTraceKeys(traceKeys);
      setTraceLabels({});
      setTracePairs(pairs);
      return;
    }

  }, [viewsClient, viewsScope, traceOptions]);

  useEffect(() => {
    if (savedViews.busy || viewsClient.state.busy) return;
    const id = pendingTraceViewIdRef.current;
    if (id && isBuiltInTraceViewId(id) && traceOptions.length === 0) return;
    if (id) {
      // Fetch the definition from the server, including selections beyond the first metadata page.
      // Clear before dispatch so a missing/offline response cannot cause an automatic retry loop.
      pendingTraceViewIdRef.current = null;
      void selectTraceView(id);
    }
  }, [selectTraceView, traceOptions.length, savedViews.busy, viewsClient, viewsScope]);

  async function saveTraceView(asNew = false) {
    const name = traceViewName.trim();
    if (!name || enabledTraceKeys.length === 0) return;
    if (selectedTraceViewId.startsWith('browser:') && (!importConsent || importScope !== viewsScope)) return;
    const revision = !asNew && loadedTraceView?.id === selectedTraceViewId ? loadedTraceView.revision : null;
    if (asNew && savedViews.conflict) pendingCreate.current.clear();
    const currentKeys = new Set(enabledTraceKeys);
    const definition = {
      name, traceKeys: enabledTraceKeys, labels: traceLabels, pairs: tracePairs,
      units: { ...traceUnits, ...Object.fromEntries(traceOptions
        .filter((option) => currentKeys.has(option.key) && option.unit)
        .map((option) => [option.key, option.unit!])) },
    };
    const id = revision === null ? pendingCreate.current.forDraft(definition) : selectedTraceViewId;
    const view = await viewsClient.save(id, definition, revision);
    if (!view) return;
    pendingCreate.current.clear();
    setLoadedTraceView(view);
    setSelectedTraceViewId(view.id);
    writeSelectedCloudTraceView(viewsScope, view.id);
    pendingTraceViewIdRef.current = null;
    onSaveDialogClose?.();
    await viewsClient.list();
  }

  async function deleteTraceView() {
    if (!loadedTraceView || loadedTraceView.id !== selectedTraceViewId) return;
    const result = await viewsClient.remove(loadedTraceView.id, loadedTraceView.revision);
    if (!result) return;
    writeSelectedCloudTraceView(viewsScope, null);
    setLoadedTraceView(null);
    setSelectedTraceViewId('');
    pendingTraceViewIdRef.current = null;
    await viewsClient.list();
  }

  async function importLegacyView() {
    if (!legacyImport) return;
    const result = await viewsClient.importLegacy(legacyImport, importScope, importConsent);
    if (!result) return;
    setLegacyImport(null);
    setImportConsent(false);
    setImportNotice(result.status === 'deleted'
      ? 'This browser save was previously imported and deleted in this account. It has not been restored. The browser copy remains available.'
      : result.status === 'existing' ? 'Already imported. The account version was kept unchanged.' : 'Imported to your account. The browser copy was retained.');
    await viewsClient.list();
  }

  function moveTrace(key: string, direction: -1 | 1) {
    pendingTraceViewIdRef.current = null;
    setEnabledTraceKeys((current) => {
      const index = current.indexOf(key);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[nextIndex]] = [next[nextIndex]!, next[index]!];
      return next;
    });
  }

  function pairTraceWithPrevious(key: string, paired: boolean) {
    pendingTraceViewIdRef.current = null;
    setTracePairs((current) => pairSavedTraceWithPrevious(enabledTraceKeys, current, key, paired));
  }

  function dropTrace(targetKey: string) {
    if (!draggedTraceKey || draggedTraceKey === targetKey) {
      setDraggedTraceKey(null);
      return;
    }

    pendingTraceViewIdRef.current = null;
    setEnabledTraceKeys((current) => {
      const withoutDragged = current.filter((key) => key !== draggedTraceKey);
      const targetIndex = withoutDragged.indexOf(targetKey);
      if (targetIndex === -1) return current;
      withoutDragged.splice(targetIndex, 0, draggedTraceKey);
      return withoutDragged;
    });
    setDraggedTraceKey(null);
  }

  function toggleTraceGroup(label: string) {
    setExpandedTraceGroups((current) => {
      const next = new Set(current);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }

  if (!primaryLayer) {
    return null;
  }

  const visibleTraceRowCount = enabledTraceOptions.filter(
    (option) => !pairedSecondaryKeys.has(option.key),
  ).length;
  const { chartHeight: defaultChartHeight } = defaultTraceChartDimensions(fillHeight);
  const maximumChartHeight = fillHeight ? MAX_TRACE_GRAPH_HEIGHT : 320;
  const sizingForTrace = (key: string) => {
    const memoryKey = traceChartMemoryKey(key, fillHeight);
    const chartHeight = clampTraceChartHeight(
      traceGraphHeights[memoryKey] ?? defaultChartHeight,
      fillHeight,
    );
    const updateHeight = (height: number | null) => {
      setTraceGraphHeights((current) => {
        const next = { ...current };
        if (height === null) delete next[memoryKey];
        else next[memoryKey] = clampTraceChartHeight(height, fillHeight);
        writeTraceGraphHeights(next);
        return next;
      });
    };

    return {
      chartHeight,
      rowMinHeight: chartHeight + (fillHeight ? 16 : 8),
      maximumChartHeight,
      onChartHeightChange: (height: number) => updateHeight(height),
      onChartHeightReset: () => updateHeight(null),
    };
  };
  const viewStartSec = traceZoomRange?.startSec ?? 0;
  const viewEndSec = traceZoomRange?.endSec ?? traceDurationSec;
  const viewSpanSec = Math.max(0, viewEndSec - viewStartSec);
  const zoomFactor = viewSpanSec > 0 ? traceDurationSec / viewSpanSec : 1;
  const canZoomIn = traceDurationSec > MIN_TRACE_ZOOM_SPAN_SEC
    && viewSpanSec > MIN_TRACE_ZOOM_SPAN_SEC + 0.01;
  const compactPanelHeight = drawer
    ? Math.min(420, Math.max(90, 34 + visibleTraceRowCount * 52))
    : 240;

  return (
    <Box
      data-trace-alignment={alignmentMode}
      sx={{
        ...tracePanelSx(isDark, drawer),
        display: 'flex',
        flexDirection: 'column',
        height: fillHeight ? '100%' : compactPanelHeight,
        ...(fillHeight ? { flex: 1, maxHeight: 'none' } : {}),
      }}
    >
      <Stack direction="row" sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', alignItems: 'stretch' }}>
        <Box
          sx={{
            minWidth: 0,
            minHeight: 0,
            height: '100%',
            flex: 1,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            scrollbarGutter: 'stable',
            pr: 0.25,
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: 999,
              bgcolor: isDark ? 'rgba(148,163,184,0.38)' : 'rgba(100,116,139,0.32)',
            },
          }}
        >
          <Stack
            direction="row"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 6,
              minHeight: 32,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.35,
              px: 0.2,
              bgcolor: chrome.panelBg,
            }}
          >
            <Typography sx={{ fontSize: '0.74rem', color: chrome.sectionLabel, fontWeight: 850, letterSpacing: '0.05em' }}>
              LAP TRACES
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              {selectedRange && onReplaySelection ? (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ReplayRoundedIcon sx={{ fontSize: 15 }} />}
                    onClick={onReplaySelection}
                    sx={{
                      height: 26,
                      px: 0.9,
                      borderRadius: '6px',
                      bgcolor: '#2563EB',
                      color: '#FFFFFF',
                      textTransform: 'none',
                      fontSize: '0.68rem',
                      fontWeight: 850,
                      '&:hover': { bgcolor: '#1D4ED8' },
                    }}
                  >
                    Replay {formatTraceClock(selectedRange.endSec - selectedRange.startSec)}
                  </Button>
                  {onClearSelection ? (
                    <MuiTooltip title="Clear selection" arrow>
                      <IconButton size="small" onClick={onClearSelection} sx={{ p: 0.35, color: chrome.meta }}>
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </MuiTooltip>
                  ) : null}
                </>
              ) : null}
              <MuiTooltip
                title="Pinch or use a mouse wheel over a line graph to zoom. Two-finger scrolling moves the page."
                placement="top"
                arrow
              >
                <ButtonGroup
                  size="small"
                  variant="outlined"
                  aria-label="Line graph zoom"
                  sx={{
                    height: 24,
                    '& .MuiButton-root': {
                      minWidth: 27,
                      height: 24,
                      px: 0.35,
                      borderColor: chrome.chartRowBorder,
                      color: chrome.meta,
                      fontSize: '0.64rem',
                      fontWeight: 850,
                      lineHeight: 1,
                      textTransform: 'none',
                    },
                  }}
                >
                  <Button
                    aria-label="Zoom out line graphs"
                    title="Zoom out line graphs"
                    disabled={!traceZoomRange}
                    onClick={() => setTraceZoomRange((current) => (
                      nextTraceZoomRange(current, traceDurationSec, cursorSec, 'out')
                    ))}
                  >
                    <ZoomOutIcon sx={{ fontSize: 15 }} />
                  </Button>
                  <Button
                    aria-label="Reset line graph zoom"
                    title="Reset line graph zoom"
                    disabled={!traceZoomRange}
                    onClick={() => setTraceZoomRange(null)}
                    sx={{ minWidth: '38px !important', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {zoomFactor.toFixed(zoomFactor >= 10 ? 0 : 1).replace('.0', '')}×
                  </Button>
                  <Button
                    aria-label="Zoom in line graphs"
                    title="Zoom in line graphs"
                    disabled={!canZoomIn}
                    onClick={() => setTraceZoomRange((current) => (
                      nextTraceZoomRange(current, traceDurationSec, cursorSec, 'in')
                    ))}
                  >
                    <ZoomInIcon sx={{ fontSize: 15 }} />
                  </Button>
                </ButtonGroup>
              </MuiTooltip>
              <Typography sx={{ fontSize: '0.74rem', color: chrome.meta, fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}>
                {formatTraceClock(Math.min(cursorSec, traceDurationSec))} / {formatTraceClock(traceDurationSec)}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1.5} sx={{ px: 1.5, py: 0.5 }}>
            <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.7rem', color: chrome.meta }}>
                {alignmentMode === 'gps' ? 'GPS position · primary lap reference' : 'Elapsed time'}
              </Typography>
              <MuiTooltip
                title={(
                  <Box>
                    <Typography component="div" sx={{ fontSize: '0.72rem' }}>Dashed lines mark lap finishes.</Typography>
                    <Typography component="div" sx={{ fontSize: '0.72rem' }}>Speed Δ is speed difference, not time gained.</Typography>
                  </Box>
                )}
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  aria-label="About trace chart markings"
                  sx={{ p: 0.2, color: chrome.meta }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </MuiTooltip>
            </Stack>
            {layers.map((layer, index) => {
              const gap = layer.analysis.durationSec - primaryLayer.analysis.durationSec;
              return <Typography key={layer.id} sx={{ fontSize: '0.7rem', color: layer.color, fontVariantNumeric: 'tabular-nums' }}>
                {layer.label}: {formatTraceClock(layer.analysis.durationSec)}{index > 0 ? ` (${gap >= 0 ? '+' : ''}${gap.toFixed(3)} s vs ${primaryLayer.label})` : ''}
              </Typography>;
            })}
          </Stack>

          {enabledTraceOptions.length === 0 ? (
            <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 120 }}>
              <Typography sx={{ fontSize: '0.78rem', color: chrome.emptyText }}>Choose traces to show.</Typography>
            </Box>
          ) : null}

          {enabledTraceOptions.map((option) => {
            if (pairedSecondaryKeys.has(option.key)) return null;

            const paired = pairedTraceData[option.key];
            const secondaryOption = paired
              ? traceOptions.find((entry) => entry.key === paired.secondaryKey)
              : null;
            if (paired && secondaryOption) {
              return (
                <TraceChartShell
                  key={option.key}
                  {...sizingForTrace(`paired:${option.key}:${secondaryOption.key}`)}
                  title={traceLabels[option.key] || `${option.label} + ${traceLabels[secondaryOption.key] || secondaryOption.label}`}
                  subtitle={`${option.label} · ${secondaryOption.label}`}
                  liveValue={`${layers.length * 2} traces`}
                  chrome={chrome}
                  traceKey={option.key}
                  dragging={draggedTraceKey === option.key}
                  onTraceDragStart={setDraggedTraceKey}
                  onTraceDragEnd={() => setDraggedTraceKey(null)}
                  onTraceDrop={dropTrace}
                  onRemove={() => removeTrace(option.key)}
                  legendItems={[
                    {
                      label: traceLabels[option.key] || option.label,
                      color: option.color,
                    },
                    {
                      label: traceLabels[secondaryOption.key] || secondaryOption.label,
                      color: secondaryOption.color,
                    },
                    ...layers.map((layer, layerIndex) => ({
                      label: layer.label,
                      color: chrome.title,
                      dashPattern: LAP_LINE_DASHES[layerIndex % LAP_LINE_DASHES.length],
                    })),
                  ]}
                >
                  <InteractiveTraceFrame
                    cursorSec={cursorSec}
                    viewStartSec={viewStartSec}
                    viewEndSec={viewEndSec}
                    onSeekSec={handleChartClick}
                    labelForSec={(elapsedSec) => formatTraceClock(elapsedSec)}
                    chrome={chrome}
                    selectedRange={selectedRange}
                    onSelectRange={onSelectRange}
                    onZoomAtSec={handleTraceZoom}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={paired.rows} margin={TRACE_CHART_MARGIN}>
                        <CartesianGrid stroke={chrome.gridStroke} vertical={false} />
                        <XAxis dataKey="elapsedSec" type="number" domain={[viewStartSec, viewEndSec]} allowDataOverflow tickFormatter={formatTraceClock} tick={{ fontSize: 10 }} height={18} minTickGap={45} />
                        {layers.map((layer) => (
                          <ReferenceLine yAxisId="primary" key={`finish:${layer.id}`} x={finishSecForLayer(layer)} stroke={layer.color} strokeDasharray="3 3" />
                        ))}
                        <YAxis yAxisId="primary" domain={paired.primaryDomain} hide />
                        <YAxis yAxisId="secondary" domain={paired.secondaryDomain} hide />
                        {layers.flatMap((layer, layerIndex) => [
                          <Line
                            key={`primary:${layer.id}`}
                            yAxisId="primary"
                            type="monotone"
                            dataKey={`primary:${layer.id}`}
                            stroke={option.color}
                            strokeDasharray={LAP_LINE_DASHES[layerIndex % LAP_LINE_DASHES.length]}
                            strokeWidth={layerIndex === 0 ? 2.2 : 1.9}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls={false}
                          />,
                          <Line
                            key={`secondary:${layer.id}`}
                            yAxisId="secondary"
                            type="monotone"
                            dataKey={`secondary:${layer.id}`}
                            stroke={secondaryOption.color}
                            strokeDasharray={LAP_LINE_DASHES[layerIndex % LAP_LINE_DASHES.length]}
                            strokeWidth={layerIndex === 0 ? 2.2 : 1.9}
                            dot={false}
                            isAnimationActive={false}
                            connectNulls={false}
                          />,
                        ])}
                      </LineChart>
                    </ResponsiveContainer>
                  </InteractiveTraceFrame>
                </TraceChartShell>
              );
            }

            if (option.kind === 'histogram') {
              const rows = option.histogramMetric ? histogramTraceData[option.histogramMetric] : [];
              if (rows.length === 0) {
                return null;
              }

              return (
                <TraceChartShell
                  key={option.key}
                  {...sizingForTrace(option.key)}
                  title={traceLabels[option.key] || option.label}
                  subtitle={option.histogramMetric === 'speed' ? 'Seconds by speed range' : 'Seconds by RPM band'}
                  liveValue="sec"
                  chrome={chrome}
                  traceKey={option.key}
                  dragging={draggedTraceKey === option.key}
                  onTraceDragStart={setDraggedTraceKey}
                  onTraceDragEnd={() => setDraggedTraceKey(null)}
                  onTraceDrop={dropTrace}
                  onRemove={() => removeTrace(option.key)}
                  legendItems={layers.map((layer) => ({ label: layer.label, color: layer.color }))}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rows} margin={TRACE_CHART_MARGIN} barGap={1} barCategoryGap={2}>
                      <CartesianGrid stroke={chrome.gridStroke} vertical={false} />
                      <XAxis dataKey="bucket" hide />
                      <YAxis hide />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        labelFormatter={(value) => `${value}`}
                        formatter={(value: number, name: string) => {
                          const layer = layers.find((entry) => entry.id === name);
                          return [`${value.toFixed(1)} sec`, layer?.label ?? name];
                        }}
                      />
                      {layers.map((layer) => (
                        <Bar
                          key={layer.id}
                          dataKey={layer.id}
                          fill={layers.length === 1 ? option.color : layer.color}
                          fillOpacity={layer.id === primaryLayer.id ? 0.82 : 0.62}
                          isAnimationActive={false}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </TraceChartShell>
              );
            }

            if (option.kind === 'speed-delta') {
              return speedDeltaTraces.flatMap(({ layer, data }) => {
                if (data.length === 0) {
                  return [];
                }

                const signedSpeedDeltaData = splitSpeedDeltaTrace(data);
                const liveDelta = liveSpeedDeltas.find((entry) => entry.layer.id === layer.id)?.deltaKph ?? null;
                const deltaDomain = autoSeriesTraceDomain(
                  data.map((point, index) => ({
                    index,
                    elapsedSec: point.elapsedSec,
                    timecodeMs: 0,
                    value: point.deltaKph,
                  })),
                  'WSpd',
                );
                const speedDeltaLabelForSec = (elapsedSec: number) => {
                  const deltaKph = interpolatedSpeedDelta(data, elapsedSec);
                  if (deltaKph === null) {
                    return formatTraceClock(elapsedSec);
                  }

                  return `${formatTraceClock(elapsedSec)} | ${formatSpeedDeltaFromKph(deltaKph, measurementPreferences, 1)}`;
                };

                return (
                  <TraceChartShell
                    key={`${option.key}-${layer.id}`}
                    {...sizingForTrace(`${option.key}:${layer.id}`)}
                    title={traceLabels[option.key] || 'Speed Δ'}
                    subtitle={`${layer.label} vs ${primaryLayer.label}`}
                    liveValue={
                      liveDelta !== null
                        ? formatSpeedDeltaFromKph(liveDelta, measurementPreferences, 0)
                        : '—'
                    }
                    chrome={chrome}
                    traceKey={option.key}
                    dragging={draggedTraceKey === option.key}
                    onTraceDragStart={setDraggedTraceKey}
                    onTraceDragEnd={() => setDraggedTraceKey(null)}
                    onTraceDrop={dropTrace}
                    onRemove={() => removeTrace(option.key)}
                    legendItems={[
                      { label: 'Faster', color: SPEED_DELTA_POSITIVE_COLOR },
                      { label: 'Slower', color: SPEED_DELTA_NEGATIVE_COLOR },
                    ]}
                  >
                    <InteractiveTraceFrame
                      cursorSec={cursorSec}
                      viewStartSec={viewStartSec}
                      viewEndSec={viewEndSec}
                      onSeekSec={handleChartClick}
                      labelForSec={speedDeltaLabelForSec}
                      chrome={chrome}
                      selectedRange={selectedRange}
                      onSelectRange={onSelectRange}
                      onZoomAtSec={handleTraceZoom}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={signedSpeedDeltaData} margin={TRACE_CHART_MARGIN}>
                          <CartesianGrid stroke={chrome.gridStroke} vertical={false} />
                          <XAxis dataKey="elapsedSec" type="number" domain={[viewStartSec, viewEndSec]} allowDataOverflow tickFormatter={formatTraceClock} tick={{ fontSize: 10 }} height={18} minTickGap={45} />
                        {layers.map((layer) => (
                          <ReferenceLine key={`finish:${layer.id}`} x={finishSecForLayer(layer)} stroke={layer.color} strokeDasharray="3 3" />
                        ))}
                          <YAxis domain={deltaDomain} hide />
                          <Tooltip
                            contentStyle={chartTooltipStyle}
                            labelFormatter={(value) => formatTraceClock(Number(value))}
                            formatter={(value: number) => [
                              formatSpeedDeltaFromKph(value, measurementPreferences, 1),
                              'Δ speed',
                            ]}
                          />
                          <ReferenceLine
                            y={0}
                            stroke={chrome.cursorLine}
                            strokeWidth={1.6}
                            strokeDasharray="8 5"
                          />
                          <Area
                            type="monotone"
                            dataKey="deltaPos"
                            stroke="none"
                            fill={SPEED_DELTA_POSITIVE_COLOR}
                            fillOpacity={0.42}
                            isAnimationActive={false}
                          />
                          <Area
                            type="monotone"
                            dataKey="deltaNeg"
                            stroke="none"
                            fill={SPEED_DELTA_NEGATIVE_COLOR}
                            fillOpacity={0.38}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="deltaPos"
                            stroke={SPEED_DELTA_POSITIVE_COLOR}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="deltaNeg"
                            stroke={SPEED_DELTA_NEGATIVE_COLOR}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </InteractiveTraceFrame>
                  </TraceChartShell>
                );
              });
            }

            const isGroundSpeed = option.kind === 'ground-speed';
            const isGpsCoordinate = option.kind === 'gps-coordinate';
            const trace = isGroundSpeed
              ? groundSpeedTrace
              : isGpsCoordinate
                ? gpsCoordinateTraceData[option.key] ?? []
                : seriesTraceData[option.key] ?? [];
            const traceShortName = option.shortName ?? (isGroundSpeed ? 'WSpd' : undefined);
            if (trace.length === 0 || !traceShortName) {
              return (
                <TraceChartShell
                  key={option.key}
                  {...sizingForTrace(option.key)}
                  title={traceLabels[option.key] || option.label}
                  subtitle={option.shortName ?? option.sublabel}
                  liveValue="—"
                  chrome={chrome}
                  traceKey={option.key}
                  dragging={draggedTraceKey === option.key}
                  onTraceDragStart={setDraggedTraceKey}
                  onTraceDragEnd={() => setDraggedTraceKey(null)}
                  onTraceDrop={dropTrace}
                  onRemove={() => removeTrace(option.key)}
                  onAddTrace={(anchor) => {
                    setPairMenuPrimaryKey(option.key);
                    setPairMenuAnchor(anchor);
                    setPairSearch('');
                  }}
                  legendItems={[{ label: option.label, color: option.color }]}
                >
                  <Box
                    sx={{
                      height: '100%',
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: '6px',
                      bgcolor: isDark ? 'rgba(255,255,255,0.025)' : '#F8FAFC',
                      color: chrome.emptyText,
                      fontSize: '0.72rem',
                      fontWeight: 750,
                    }}
                  >
                    No data in this lap
                  </Box>
                </TraceChartShell>
              );
            }

            const domain = autoSeriesTraceDomain(
              trace.flatMap((row, index) =>
                layers.map((layer) => ({
                  index,
                  elapsedSec: row.elapsedSec,
                  timecodeMs: 0,
                  value: typeof row[layer.id] === 'number' && Number.isFinite(row[layer.id]) ? row[layer.id]! : null,
                })),
              ),
              traceShortName,
            );
            const liveValues = isGroundSpeed
              ? liveGroundSpeedValues
              : isGpsCoordinate
                ? liveGpsCoordinateValuesByTrace.get(option.key)
                : liveSeriesValuesByTrace.get(option.key);
            const primaryLiveValue = liveValues?.get(primaryLayer.id) ?? null;

            return (
              <TraceChartShell
                key={option.key}
                {...sizingForTrace(option.key)}
                title={traceLabels[option.key] || option.label}
                subtitle={layers.map((layer) => layer.label).join(' | ')}
                liveValue={
                  primaryLiveValue !== null
                    ? formatSeriesTraceValue(
                        traceShortName,
                        primaryLiveValue,
                        option.unit ?? '',
                        option.decimals ?? 2,
                        measurementPreferences,
                      )
                    : '—'
                }
                chrome={chrome}
                traceKey={option.key}
                dragging={draggedTraceKey === option.key}
                onTraceDragStart={setDraggedTraceKey}
                onTraceDragEnd={() => setDraggedTraceKey(null)}
                onTraceDrop={dropTrace}
                onRemove={() => removeTrace(option.key)}
                onAddTrace={(anchor) => {
                  setPairMenuPrimaryKey(option.key);
                  setPairMenuAnchor(anchor);
                  setPairSearch('');
                }}
                legendItems={layers.length === 1
                  ? [{ label: option.label, color: option.color }]
                  : layers.map((layer) => ({ label: layer.label, color: layer.color }))}
              >
                <InteractiveTraceFrame
                  cursorSec={cursorSec}
                  viewStartSec={viewStartSec}
                  viewEndSec={viewEndSec}
                  onSeekSec={handleChartClick}
                  labelForSec={(elapsedSec) => seriesLabelForSec(trace, option, elapsedSec)}
                  chrome={chrome}
                  selectedRange={selectedRange}
                  onSelectRange={onSelectRange}
                  onZoomAtSec={handleTraceZoom}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trace} margin={TRACE_CHART_MARGIN}>
                      <CartesianGrid stroke={chrome.gridStroke} vertical={false} />
                      <XAxis dataKey="elapsedSec" type="number" domain={[viewStartSec, viewEndSec]} allowDataOverflow tickFormatter={formatTraceClock} tick={{ fontSize: 10 }} height={18} minTickGap={45} />
                        {layers.map((layer) => (
                          <ReferenceLine key={`finish:${layer.id}`} x={finishSecForLayer(layer)} stroke={layer.color} strokeDasharray="3 3" />
                        ))}
                      <YAxis domain={domain} hide />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        labelFormatter={(value) => formatTraceClock(Number(value))}
                        formatter={(value: number, name: string) => {
                          const layer = layers.find((entry) => entry.id === name);
                          return [
                            formatSeriesTraceValue(
                              traceShortName,
                              value,
                              option.unit ?? '',
                              option.decimals ?? 2,
                              measurementPreferences,
                            ),
                            layer?.label ?? name,
                          ];
                        }}
                      />
                      {layers.map((layer) => (
                        <Line
                          key={layer.id}
                          type={traceShortName === 'RPM' ? 'linear' : 'monotone'}
                          dataKey={layer.id}
                          stroke={layers.length === 1 ? option.color : layer.color}
                          strokeWidth={layer.id === primaryLayer.id ? 2.15 : 1.95}
                          strokeOpacity={layer.id === primaryLayer.id ? 1 : 0.96}
                          dot={false}
                          isAnimationActive={false}
                          connectNulls={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </InteractiveTraceFrame>
              </TraceChartShell>
            );
          })}

        </Box>

      </Stack>

      <Menu
        anchorEl={pairMenuAnchor}
        open={Boolean(pairMenuAnchor && pairMenuPrimaryKey)}
        onClose={() => {
          setPairMenuAnchor(null);
          setPairMenuPrimaryKey(null);
          setPairSearch('');
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 310,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: 420,
              bgcolor: chrome.pickerBg,
              color: chrome.title,
              border: `1px solid ${chrome.pickerBorder}`,
              boxShadow: isDark ? '0 18px 42px rgba(0,0,0,0.5)' : '0 16px 36px rgba(15,23,42,0.16)',
            },
          },
        }}
      >
        <Box sx={{ position: 'sticky', top: 0, zIndex: 2, px: 1, pt: 0.5, pb: 0.75, bgcolor: chrome.pickerBg }}>
          <Typography sx={{ px: 0.25, mb: 0.65, fontSize: '0.72rem', fontWeight: 900 }}>
            Add to {pairMenuPrimary?.label ?? 'chart'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search channels"
            value={pairSearch}
            onChange={(event) => setPairSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{ '& .MuiInputBase-root': { height: 34, fontSize: '0.74rem' } }}
          />
        </Box>
        {pairMenuOptions.length > 0 ? pairMenuOptions.map((option) => (
          <MenuItem
            key={option.key}
            onClick={() => addTraceToGraph(option.key)}
            sx={{ minHeight: 48, gap: 0.8, px: 1.25 }}
          >
            <Box sx={{ width: 16, height: 3, borderRadius: 999, bgcolor: option.color, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography noWrap sx={{ fontSize: '0.76rem', fontWeight: 850 }}>
                {option.label}
              </Typography>
              <Typography noWrap sx={{ fontSize: '0.64rem', color: chrome.pickerItemSubtext }}>
                {option.sublabel}
              </Typography>
            </Box>
          </MenuItem>
        )) : (
          <Typography sx={{ px: 1.25, py: 2, fontSize: '0.72rem', color: chrome.pickerItemSubtext }}>
            No matching channels
          </Typography>
        )}
      </Menu>

      <Dialog
        open={saveDialogOpen}
        onClose={onSaveDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 0.75, fontWeight: 900 }}>Save trace view</DialogTitle>
        <DialogContent>
          <Stack spacing={1.2} sx={{ pt: 0.5 }}>
            <Typography variant="body2">Saved privately to {savedViews.user?.email ?? savedViews.user?.id ?? 'your account'} in workspace {savedViews.user?.teamId ?? savedViews.user?.id ?? 'none'}.</Typography>
            {selectedTraceViewId.startsWith('browser:') ? <Stack direction="row" alignItems="center"><Checkbox checked={importConsent && importScope === viewsScope} onChange={(event) => { setImportConsent(event.target.checked); setImportScope(viewsScope); }} inputProps={{ 'aria-label': 'This browser save belongs to me' }} /><Typography>This browser save belongs to me; save it to the account shown above.</Typography></Stack> : null}
            {savedViews.error ? <Typography role="alert" color="error">{savedViews.error}</Typography> : null}
            {savedViews.conflict && (loadedTraceView || pendingCreate.current.id) ? <Button disabled={savedViews.busy} onClick={() => void selectTraceView(loadedTraceView?.id ?? pendingCreate.current.id!)}>Reload saved version (replace draft)</Button> : null}
            <TextField
              fullWidth
              size="small"
              label="View name"
              placeholder="Race analysis"
              value={traceViewName}
              onChange={(event) => setTraceViewName(event.target.value)}
            />
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
              Reorder charts, rename them, or combine a channel with the chart above. Each chart supports two channels.
            </Typography>
            <Stack spacing={0.65}>
              {enabledTraceKeys.map((key, index) => {
                const option = traceOptions.find((entry) => entry.key === key);
                if (!option) return <Typography key={key} variant="body2">{traceLabels[key] || key}: unavailable in this session; retained in the saved view.</Typography>;
                const previousOption = index > 0 ? traceOptions.find((entry) => entry.key === enabledTraceKeys[index - 1]) : null;
                const supportsPair = traceOptionSupportsPair(option);
                const previousSupportsPair = traceOptionSupportsPair(previousOption);
                const pairedWithPrevious = Object.values(tracePairs).includes(key);
                const pairingDisabled = !supportsPair || !previousSupportsPair || Boolean(previousOption && tracePairs[previousOption.key]);

                return (
                  <Stack
                    key={key}
                    direction="row"
                    spacing={0.7}
                    sx={{ alignItems: 'center', p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}
                  >
                    <Stack direction="row" spacing={0.15}>
                      <IconButton size="small" disabled={index === 0} onClick={() => moveTrace(key, -1)} aria-label={`Move ${option.label} up`}>
                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" disabled={index === enabledTraceKeys.length - 1} onClick={() => moveTrace(key, 1)} aria-label={`Move ${option.label} down`}>
                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                    <TextField
                      size="small"
                      label="Chart name"
                      value={traceLabels[key] ?? option.label}
                      onChange={(event) => setTraceLabels((current) => ({ ...current, [key]: event.target.value }))}
                      sx={{ flex: 1 }}
                    />
                    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', minWidth: 118 }}>
                      <Checkbox
                        size="small"
                        checked={pairedWithPrevious}
                        disabled={pairingDisabled && !pairedWithPrevious}
                        onChange={(event) => pairTraceWithPrevious(key, event.target.checked)}
                      />
                      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.15 }}>
                        Same chart as above
                      </Typography>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {loadedTraceView ? (
            <Button color="error" disabled={savedViews.busy || !loadedTraceView} onClick={() => void deleteTraceView()} sx={{ mr: 'auto', textTransform: 'none' }}>Delete view</Button>
          ) : null}
          {loadedTraceView || savedViews.conflict ? <Button disabled={savedViews.busy || !viewsScope || !traceViewName.trim() || enabledTraceKeys.length === 0 || (selectedTraceViewId.startsWith('browser:') && (!importConsent || importScope !== viewsScope))} onClick={() => void saveTraceView(true)}>Save draft as new view</Button> : null}
          <Button color="inherit" onClick={onSaveDialogClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={savedViews.busy || !viewsScope || !traceViewName.trim() || enabledTraceKeys.length === 0 || (selectedTraceViewId.startsWith('browser:') && (!importConsent || importScope !== viewsScope))}
            onClick={() => void saveTraceView()}
            sx={{ textTransform: 'none', fontWeight: 850 }}
          >
            {loadedTraceView
              ? 'Update view'
              : 'Save as new view'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(legacyImport)} onClose={() => setLegacyImport(null)} fullWidth maxWidth="sm">
        <DialogTitle>Review browser save</DialogTitle>
        <DialogContent>
          <Typography>{legacyImport?.name}</Typography>
          <Typography variant="body2">Ordered channels: {legacyImport?.traceKeys.join(', ')}</Typography>
          <Typography variant="body2">Destination account: {savedViews.user?.email ?? savedViews.user?.id ?? 'Not signed in'}. Workspace: {savedViews.user?.teamId ?? savedViews.user?.id ?? 'None'}.</Typography>
          <Typography variant="body2">This browser record has no recorded owner. Import only if it belongs to you. Existing account versions are never overwritten.</Typography>
          <Stack direction="row" alignItems="center"><Checkbox checked={importConsent} onChange={(event) => setImportConsent(event.target.checked)} inputProps={{ 'aria-label': 'This browser save belongs to me' }} /><Typography>This browser save belongs to me; import it into the account and workspace shown above.</Typography></Stack>
          {savedViews.error ? <Typography role="alert" color="error">{savedViews.error}</Typography> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegacyImport(null)}>Cancel</Button>
          <Button disabled={savedViews.busy} onClick={() => {
            if (!legacyImport) return;
            selectionRequest.current += 1;
            pendingCreate.current.clear();
            pendingTraceViewIdRef.current = null;
            setLoadedTraceView(null);
            setSelectedTraceViewId(`browser:${legacyImport.id}`);
            writeSelectedCloudTraceView(viewsScope, null);
            setTraceViewName(legacyImport.name);
            setEnabledTraceKeys(legacyImport.traceKeys);
            setTraceLabels(legacyImport.labels ?? {});
            setTracePairs(legacyImport.pairs ?? {});
            setTraceUnits(legacyImport.units ?? {});
            setImportConsent(false);
            setLegacyImport(null);
          }}>Open browser copy without uploading</Button>
          <Button disabled={savedViews.busy || !viewsScope || importScope !== viewsScope || !importConsent} onClick={() => void importLegacyView()}>Import selected save</Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor="right"
        open={tracePickerOpen}
        onClose={onTracePickerClose}
        sx={{ '& .MuiBackdrop-root': { bgcolor: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(15,23,42,0.18)' } }}
        ModalProps={{
          disableEnforceFocus: true,
          disableScrollLock: true,
        }}
        PaperProps={{
          sx: tracePickerDrawerPaperSx(isDark),
        }}
      >
        <Box sx={{ px: 1.5, py: 1.35, borderBottom: `1px solid ${chrome.headerBorder}` }}>
          <Typography sx={{ fontSize: '0.96rem', fontWeight: 900, color: chrome.title }}>Configure trace view</Typography>
          <Typography sx={{ mt: 0.2, fontSize: '0.7rem', color: chrome.subtitle }}>
            Choose any number of channels, then save the layout for later.
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.2, py: 1.2 }}>
          <Stack spacing={1.15}>
            <TextField
              select
              fullWidth
              size="small"
              label="Saved trace view"
              value={selectedTraceViewId}
              disabled={savedViews.busy}
              onChange={(event) => void selectTraceView(event.target.value)}
            >
              <MenuItem value="">New trace view</MenuItem>
              {selectedTraceViewId.startsWith('browser:') ? <MenuItem value={selectedTraceViewId}>Browser copy: {traceViewName}</MenuItem> : null}
              {BUILT_IN_ENGINE_TRACE_PRESETS.map((preset) => (
                <MenuItem key={preset.id} value={preset.id}>
                  {preset.name} · Built-in preset
                </MenuItem>
              ))}
              {loadedTraceView && !savedTraceViews.some((view) => view.id === loadedTraceView.id) ? <MenuItem value={loadedTraceView.id}>{loadedTraceView.name}</MenuItem> : null}
              {savedTraceViews.map((view) => (
                <MenuItem key={view.id} value={view.id}>{view.name}</MenuItem>
              ))}
            </TextField>
            {savedViews.busy ? <Typography role="status">Loading saved views...</Typography> : null}
            {savedViews.error ? <Typography role="alert" color="error">{savedViews.error}</Typography> : null}
            {!viewsScope ? <Typography>Sign in to save views across devices.</Typography> : null}
            {enabledTraceKeys.some((key) => !traceOptions.some((option) => option.key === key)) ? <Typography variant="body2">Some saved channels are unavailable in this session. Their definitions are retained; select a session containing them to render those charts.</Typography> : null}
            <Button disabled={savedViews.busy || !viewsScope} onClick={() => void viewsClient.list()}>Refresh saved views</Button>
            {retryTraceViewId ? <Button disabled={savedViews.busy || !viewsScope} onClick={() => void selectTraceView(retryTraceViewId)}>Retry saved selection</Button> : null}
            {savedViews.nextCursor ? <Button disabled={savedViews.busy} onClick={() => void viewsClient.list(true)}>Load more saved views</Button> : null}
            {importNotice ? <Typography role="status">{importNotice}</Typography> : null}
            {legacyViews.length ? <Stack spacing={0.5}>
              <Typography variant="body2">Browser-only saves (ownership unknown). Select a save to review before importing. Original copies stay in this browser.</Typography>
              {legacyViews.map((view, index) => <Button key={`${view.id}:${index}`} disabled={savedViews.busy} onClick={() => { setLegacyImport(view); setImportConsent(false); setImportScope(viewsScope); }}>{view.name} - review browser save</Button>)}
            </Stack> : null}
            <TextField
              fullWidth
              size="small"
              label="Search channels"
              placeholder="Speed, brake, temperature…"
              value={traceSearch}
              onChange={(event) => setTraceSearch(event.target.value)}
            />
            <Typography sx={{ pt: 0.35, fontSize: '0.66rem', fontWeight: 850, color: chrome.sectionLabel, letterSpacing: '0.05em' }}>
              {allSeriesChannels.length} DATA CHANNELS
            </Typography>
            {traceOptionGroups.length === 0 ? (
              <Typography sx={{ px: 0.25, py: 1, fontSize: '0.74rem', color: chrome.emptyText }}>
                No channels match “{traceSearch}”.
              </Typography>
            ) : null}
            {traceOptionGroups.map((group) => {
              const expanded = Boolean(traceSearch.trim()) || expandedTraceGroups.has(group.label);
              return (
                <Stack key={group.label} spacing={0.45}>
                  <Button
                    color="inherit"
                    fullWidth
                    onClick={() => toggleTraceGroup(group.label)}
                    endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 140ms ease' }} />}
                    sx={{
                      minHeight: 32,
                      px: 0.75,
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      color: chrome.title,
                      bgcolor: isDark ? 'rgba(255,255,255,0.025)' : '#F8FAFC',
                      border: `1px solid ${chrome.pickerItemBorder}`,
                      fontSize: '0.7rem',
                      fontWeight: 900,
                      '& .MuiButton-endIcon': { ml: 0.5 },
                      '&:hover': { bgcolor: chrome.pickerItemHover },
                    }}
                  >
                    <Box component="span" sx={{ display: 'flex', minWidth: 0, gap: 0.5 }}>
                      <Box component="span">{group.label}</Box>
                      <Box component="span" sx={{ color: chrome.subtitle, fontWeight: 750 }}>
                        {group.availableCount}/{group.options.length}
                      </Box>
                    </Box>
                  </Button>
                  <Collapse in={expanded} timeout={140}>
                    <Stack spacing={0.45}>
                      {group.options.map((option) => {
                        const checked = enabledTraceKeys.includes(option.key);
                        return (
                          <Stack
                            key={option.key}
                            direction="row"
                            spacing={0.75}
                            onClick={() => toggleTrace(option.key)}
                            sx={{
                              alignItems: 'center',
                              px: 0.75,
                              py: 0.65,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              bgcolor: checked ? chrome.pickerItemSelectedBg : 'transparent',
                              border: checked ? `1px solid ${chrome.pickerItemSelectedBorder}` : `1px solid ${chrome.pickerItemBorder}`,
                              '&:hover': { bgcolor: checked ? chrome.pickerItemSelectedBg : chrome.pickerItemHover },
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={checked}
                              tabIndex={-1}
                              sx={{
                                p: 0,
                                color: chrome.pickerCheckbox,
                                '&.Mui-checked': { color: option.color },
                              }}
                            />
                            <Box sx={{ width: 14, height: 3, borderRadius: 999, bgcolor: option.color, flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 850, color: chrome.pickerItemText }}>
                                {option.label}
                              </Typography>
                              <Typography sx={{ fontSize: '0.68rem', color: chrome.pickerItemSubtext }} noWrap>
                                {option.sublabel}
                              </Typography>
                            </Box>
                            <MuiTooltip
                              arrow
                              placement="left"
                              enterDelay={250}
                              title={traceOptionTooltip(option)}
                            >
                              <IconButton
                                size="small"
                                aria-label={`About ${option.label}`}
                                onClick={(event) => event.stopPropagation()}
                                onMouseDown={(event) => event.stopPropagation()}
                                sx={{
                                  ml: 'auto',
                                  p: 0.35,
                                  color: chrome.pickerItemSubtext,
                                  '&:hover': { color: chrome.pickerItemText, bgcolor: chrome.pickerItemHover },
                                }}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </MuiTooltip>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Collapse>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
